import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { Analytics } from "@vercel/analytics/react";

// ─── SUPABASE CLIENT ──────────────────────────────────────────
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "https://placeholder.supabase.co";
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "placeholder-key";
const supabase = window.__ws_supabase ?? (window.__ws_supabase = createClient(supabaseUrl, supabaseKey));

// ─── ENV CONFIG ───────────────────────────────────────────────
const GATE_CODE = process.env.REACT_APP_GATE_CODE || "";
const FACILITATOR_CODE = "AZUL2026";
const REVOKED_CODES = ["BUILD01"];
// eslint-disable-next-line no-unused-vars
const ANTHROPIC_API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || "";

// ─── DESIGN TOKENS ────────────────────────────────────────────
const T = {
  bg:       "#0f1410",
  bgMid:    "#161e16",
  bgSurf:   "#1e2b1e",
  cream:    "#f5f2ec",
  sand:     "#eae5d8",
  sand2:    "#e0dcd0",
  ink:      "#1a1c18",
  ink2:     "#3d4038",
  ink3:     "#6a6d62",
  ink4:     "#a0a398",
  terra:    "#c49a2a",  // Changed to gold
  terraL:   "#f5edce",  // Changed to goldL
  terraPale:"#fdf9ed",  // Changed to goldPale
  moss:     "#4a7c62",
  mossL:    "#e8f0eb",
  gold:     "#c49a2a",
  goldL:    "#f5edce",
  purple:   "#534ab7",
  purpleL:  "#eeedfe",
  forest:   "#1e3a2f",
  white:    "#ffffff",
  rule:     "rgba(255,255,255,0.07)",
  ruleCard: "#dedad0",

  // Text colors with accessible contrast on dark backgrounds
  textLight:    "rgba(255,255,255,0.95)",  // Primary text on dark
  textMedium:   "rgba(255,255,255,0.75)",  // Secondary text on dark
  textSubtle:   "rgba(255,255,255,0.55)",  // Tertiary text on dark (min contrast)
  textDim:      "rgba(255,255,255,0.35)",  // Labels only on dark
};

const L = {
  context:    { accent: "#5a5854", bg: "#eae5d8", pale: "#f5f2ec",  label: "Context"        },
  direction:  { accent: "#4a7c62", bg: "#e8f0eb", pale: "#f2f8f5",  label: "Direction"      },
  references: { accent: "#6b4e71", bg: "#f0ebf5", pale: "#f7f4f9",  label: "Quality design" },
  system:     { accent: "#534ab7", bg: "#eeedfe", pale: "#f5f4ff",  label: "Design system"  },
  scope:      { accent: "#c49a2a", bg: "#f5edce", pale: "#fdf9ed",  label: "Scope & Success" },
  success:    { accent: "#c49a2a", bg: "#f5edce", pale: "#fdf9ed",  label: "Success"        },
};

// Typography - using Google Fonts loaded in index.html
const serif = "'Fraunces','Georgia',serif";
const sans  = "'Source Sans 3','Helvetica Neue',Helvetica,Arial,sans-serif";

// Typography scale with line heights for optimal readability
const type = {
  // Body text (larger for better readability, 1.6-1.8 line height)
  body: 24,
  bodyLh: 1.7,
  bodySm: 21,
  bodySmLh: 1.6,

  // UI text (18px for comfortable reading, 1.5 line height for compact UI)
  ui: 18,
  uiLh: 1.5,

  // Labels (14px for better legibility, uppercase/tracked)
  label: 14,
  labelLh: 1.4,

  // Headings (larger sizes with tighter line heights)
  h1: 48,
  h1Lh: 1.15,
  h2: 36,
  h2Lh: 1.2,
  h3: 28,
  h3Lh: 1.3,
  h4: 20,
  h4Lh: 1.4,
};

// Spacing scale (8px base unit for consistent rhythm)
// eslint-disable-next-line no-unused-vars
const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
};

// ─── STORAGE HELPERS ──────────────────────────────────────────
async function getSessionCode() {
  try {
    const localCode = localStorage.getItem("ws:code");
    if (localCode) return localCode;
    return "AZUL-SESSION";
  } catch {
    return "AZUL-SESSION";
  }
}

async function setSessionCode(c) {
  try {
    localStorage.setItem("ws:code", c);
    await supabase.from("sessions").upsert({ code: c }, { onConflict: "code" });
  } catch (e) {
    console.error("Set session code error:", e);
  }
}

async function saveParticipantData(name, field, data) {
  try {
    const sessionCode = await getSessionCode();
    const payload = {
      session_code: sessionCode,
      name: name,
      [field]: data,
      updated_at: new Date().toISOString()
    };
    await supabase.from("participants").upsert(payload, { onConflict: "session_code,name" });
  } catch (e) {
    console.error("Save error:", e);
  }
}

async function loadParticipantData(name, field) {
  try {
    const sessionCode = await getSessionCode();
    const selectQuery = field || "*"; // Select all fields if no specific field provided
    const { data, error } = await supabase
      .from("participants")
      .select(selectQuery)
      .eq("session_code", sessionCode)
      .eq("name", name)
      .maybeSingle();
    if (error || !data) return null;
    return field ? data[field] : data; // Return specific field or entire object
  } catch {
    return null;
  }
}

async function getAllParticipants() {
  try {
    const sessionCode = await getSessionCode();
    const { data, error } = await supabase
      .from("participants")
      .select("*")
      .eq("session_code", sessionCode)
      .order("name");
    if (error || !data) return [];
    return data;
  } catch {
    return [];
  }
}

async function deleteParticipant(name) {
  try {
    const sessionCode = await getSessionCode();
    const { error } = await supabase
      .from("participants")
      .delete()
      .eq("session_code", sessionCode)
      .eq("name", name);
    return !error;
  } catch {
    return false;
  }
}

// ─── ASSESSMENT RUBRIC ────────────────────────────────────────
const ASSESSMENT_CRITERIA = {
  intention: {
    weight: 15,
    criteria: "Clear problem definition and user understanding. Specific 'what' and 'who' answers that show real user empathy."
  },
  context: {
    weight: 18,
    criteria: "Concrete user scenario with emotional state, real constraints, and specific PRIMARY USER ACTION. Not generic."
  },
  direction: {
    weight: 25,
    criteria: "Distinctive visual direction anchored in strategic brand positioning. Shows understanding of visual tone blind spots in their brand space. Goes beyond generic adjectives to concrete visual strategy. Includes clear 'not this' boundaries and demonstrates how visual language serves positioning."
  },
  system: {
    weight: 15,
    criteria: "Specific design system with actual values (colors with hex, type sizes, spacing scale). Shows design judgment and accessibility awareness."
  },
  scope: {
    weight: 12,
    criteria: "Appropriately constrained scope. One component or screen, not trying to build everything. Clear boundaries."
  },
  success: {
    weight: 15,
    criteria: "Success defined as a feeling or user experience, not a feature list. Specific, measurable from user perspective."
  }
};

async function assessParticipant(participantData) {
  const { name, intention, prompt_layers } = participantData;

  // Build assessment prompt
  const layers = prompt_layers || {};
  const hasIntention = intention && intention.what;
  const completedLayers = Object.keys(layers).filter(k => layers[k] && layers[k].trim().length > 50).length;

  const promptText = `
VIBE CODING WORKSHOP — Work Assessment

PROJECT SUBMITTED BY: ${name}

WORKSHOP APPROACH — VIBE CODING & DESIGN:
This workshop teaches builders to think clearly, design intentionally, and prompt AI effectively. The goal is not perfection—it's clarity of thought, specificity over generic solutions, and creating distinct products that feel purposeful.

Assume the participant has completed visual exploration (research, mood boards, competitive analysis) outside this tool.

WORK SUBMITTED FOR ASSESSMENT:

Context Layer:
${layers.context || 'Not completed'}

Quality References:
${layers.references || 'Not completed'}

Design Direction:
${layers.direction || 'Not completed'}

Design System:
${layers.system || 'Not completed'}

Scope:
${layers.scope || 'Not completed'}

Success Criteria:
${layers.success || 'Not completed'}

---

ASSESSMENT TASK:

Evaluate this work objectively based on the vibe coding approach. Focus on the work itself—not the person. Assess:

1. **Clarity of thinking** — Is the idea clear and well-defined? Or vague and scattered?
2. **Specificity** — Are design decisions concrete and actionable? Or generic ("modern", "clean", "minimalist")?
3. **Design intent** — Does the work show intentional choices grounded in user needs and product goals? Or just aesthetic preferences?
4. **Prompt quality** — Would this give AI enough context and direction to build something distinct? Or would it produce generic output?
5. **Vibe coding readiness** — Can this person steer AI effectively? Do they understand what makes a prompt work?

Provide your assessment in this format:

**STATUS** (one word): strong | developing | needs-work | incomplete

**WHAT'S WORKING**
2-3 sentences on what demonstrates good design thinking and vibe coding skills.

**WHAT NEEDS ATTENTION**
2-3 sentences on gaps, generic language, or unclear thinking. Be direct and constructive.

**NEXT MOVE**
1 specific action to improve this work. Not coaching advice—a concrete edit or addition.

Be honest. Generic praise helps no one. This assessment helps the facilitator understand where each person is and what support they need.
`;

  try {
    const response = await callAI(
      "You are a design and AI-assisted development expert evaluating work from a vibe coding workshop. Assess the quality of thinking, specificity, and prompt craftsmanship. Be objective, direct, and constructive. Flag vague or generic work clearly. Celebrate specific, intentional design decisions.",
      promptText,
      600
    );

    // Parse response
    const statusMatch = response.match(/STATUS[:\s*]+(\w+)/i);
    const status = statusMatch ? statusMatch[1].toLowerCase() : 'unknown';

    return {
      status,
      analysis: response,
      completedLayers,
      totalLayers: 6,
      hasIntention
    };
  } catch (e) {
    console.error("Assessment error:", e);
    return {
      status: 'unknown',
      analysis: 'Unable to analyze at this time.',
      completedLayers,
      totalLayers: 6,
      hasIntention
    };
  }
}

// ─── AI (Supports Anthropic Claude & OpenAI) ──────────────────
async function callAI(system, user, maxTokens = 900, userApiConfig = null) {
  try {
    // Use user's API config if provided, otherwise use facilitator's
    const provider = userApiConfig?.provider || "facilitator";
    const apiKey = userApiConfig?.apiKey;

    if (provider === "openai" && apiKey) {
      // Direct call to OpenAI with user's key
      const r = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "gpt-4-turbo-preview",
          max_tokens: maxTokens,
          messages: [
            { role: "system", content: system },
            { role: "user", content: user }
          ]
        }),
      });

      if (!r.ok) {
        console.error("OpenAI error status:", r.status, r.statusText);
        try {
          const d = await r.json();
          console.error("OpenAI API error response:", d);
          if (r.status === 401) {
            return "Invalid API key. Please check your OpenAI API key in settings.";
          }
        } catch (parseError) {
          console.error("Could not parse OpenAI error response:", parseError);
        }
        return "OpenAI error. Please try again.";
      }

      const d = await r.json();
      return d.choices?.[0]?.message?.content || "Try again in a moment.";

    } else if (provider === "anthropic" && apiKey) {
      // Direct call to Anthropic with user's key
      const r = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01"
        },
        body: JSON.stringify({
          model: "claude-3-haiku-20240307",
          max_tokens: maxTokens,
          system,
          messages: [{ role: "user", content: user }]
        }),
      });

      if (!r.ok) {
        console.error("Anthropic error status:", r.status, r.statusText);
        try {
          const d = await r.json();
          console.error("Anthropic API error response:", d);
          if (r.status === 401) {
            return "Invalid API key. Please check your Anthropic API key in settings.";
          }
        } catch (parseError) {
          console.error("Could not parse Anthropic error response:", parseError);
        }
        return "Anthropic error. Please try again.";
      }

      const d = await r.json();
      return d.content?.[0]?.text || "Try again in a moment.";

    } else {
      // Use facilitator's key via proxy
      const r = await fetch("/api/anthropic", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          model: "claude-3-haiku-20240307",
          max_tokens: maxTokens,
          system,
          messages: [{ role: "user", content: user }]
        }),
      });

      // Check if response is ok before parsing JSON
      if (!r.ok) {
        console.error("API error status:", r.status, r.statusText);
        let errorMsg = "API error. Please try again.";
        try {
          const errorData = await r.json();
          console.error("API error response:", errorData);
          if (errorData.error) {
            errorMsg = errorData.error;
          }
        } catch (parseError) {
          console.error("Could not parse error response:", parseError);
        }
        return errorMsg;
      }

      const d = await r.json();
      return d.content?.[0]?.text || "Try again in a moment.";
    }
  } catch (err) {
    console.error("AI call error:", err);
    return "Connection error — check your network and try again.";
  }
}

// ─── LAYER DEFINITIONS ────────────────────────────────────────
const LAYERS = [
  {
    key: "context", num: 1,
    tagline: "Ground Claude in a real person",
    guide: "Who is this for, specifically? What are they doing right now, and why does this matter to them? PRIMARY USER ACTION: What is the single most important thing the user should do? The more concrete, the better the output.",
    example: "I'm building a sign-up page for Wonderpath, a creative workshop series for adult learners — curious, design-adjacent, not technical. They're registering on their phones, probably after seeing a post on Instagram. PRIMARY USER ACTION: Submit their name and email to join a workshop session.",
    rows: 4,
  },
  {
    key: "references", num: 2,
    tagline: "Learn from quality design",
    guide: "Add 3 URLs of products or pages whose design you admire. At least one should be relevant to your idea. AI will help you interpret what makes them work — so you can apply principles, not copy patterns.",
    example: "Reference 1: https://stripe.com/checkout\nWhat stands out: Clarity and progressive disclosure\n\nReference 2: https://airbnb.com\nWhat stands out: Visual hierarchy and trust signals",
    rows: 6,
  },
  {
    key: "direction", num: 3,
    tagline: "Set the compass before any code",
    guide: "What should this feel like? Give Claude the tone and aesthetic intention — and one good metaphor if you have one. This is not a feature list.",
    example: "This should feel like an invitation, not a form. Warm and handmade-meets-digital. Think: you're being welcomed somewhere, not processed into a database.",
    rows: 4,
  },
  {
    key: "system", num: 4,
    tagline: "Name your visual language",
    guide: "Give your system a name. Then: colors with hex values, type with sizes, component names you'll use consistently. Claude has no memory — this is the thread.",
    example: "System: Wonderpath Kit\nColors: forest #1E3A2F · cream #F5F2EC · terra #B85A35\nType: Georgia headings · Calibri body 14px\nComponents: card · input-field · button-primary · tag",
    rows: 5,
  },
  {
    key: "scope", num: 5,
    tagline: "Constrain & define success",
    guide: "SCOPE: One component or one screen — nothing more. What are you building right now, and what are you explicitly not building? Name the boundary.\n\nSUCCESS: One sentence, in human terms. Not a feature list — a feeling. What does done feel like from the inside?",
    example: "SCOPE: Build the registration card only: name field, email field, workshop selector (3 options), and a submit button. Mobile-first, no header or nav.\n\nSUCCESS: Someone fills it out and it feels like the workshop has already started.",
    scopeRows: 4,
    successRows: 3,
    combined: true,
  },
];

// ─── COLOR PALETTES ───────────────────────────────────────────
const COLOR_PALETTES = [
  { id: "forest-cream",  name: "Forest & Cream",  mood: "Warm · organic",     colors: [{ name: "forest", hex: "#1E3A2F" }, { name: "cream", hex: "#F5F2EC" }, { name: "terra", hex: "#B85A35" }],  promptLine: "Colors: forest #1E3A2F · cream #F5F2EC · terra #B85A35" },
  { id: "midnight-gold", name: "Midnight & Gold",  mood: "Executive · rich",   colors: [{ name: "midnight", hex: "#1E2761" }, { name: "ice", hex: "#CADCFC" }, { name: "gold", hex: "#E8B84B" }],   promptLine: "Colors: midnight #1E2761 · ice #CADCFC · gold #E8B84B" },
  { id: "charcoal-sage", name: "Charcoal & Sage",  mood: "Minimal · calm",    colors: [{ name: "charcoal", hex: "#36454F" }, { name: "paper", hex: "#F8F6F0" }, { name: "sage", hex: "#84B59F" }], promptLine: "Colors: charcoal #36454F · paper #F8F6F0 · sage #84B59F" },
  { id: "berry-bone",    name: "Berry & Bone",     mood: "Editorial · refined", colors: [{ name: "berry", hex: "#6D2E46" }, { name: "bone", hex: "#ECE2D0" }, { name: "rose", hex: "#A26769" }],   promptLine: "Colors: berry #6D2E46 · bone #ECE2D0 · rose #A26769" },
  { id: "ink-moss",      name: "Ink & Moss",       mood: "Studio · natural",   colors: [{ name: "ink", hex: "#1A1C18" }, { name: "sand", hex: "#EAE5D8" }, { name: "moss", hex: "#4A7C62" }],     promptLine: "Colors: ink #1A1C18 · sand #EAE5D8 · moss #4A7C62" },
  { id: "ocean-coral",   name: "Ocean & Coral",    mood: "Fresh · energetic",  colors: [{ name: "ocean", hex: "#065A82" }, { name: "foam", hex: "#E8F4F8" }, { name: "coral", hex: "#E8593C" }],  promptLine: "Colors: ocean #065A82 · foam #E8F4F8 · coral #E8593C" },
];

// ─── FONT PAIRINGS ────────────────────────────────────────────
const FONT_PAIRS = [
  {
    id: "warm-editorial",
    name: "Warm Editorial",
    mood: "Considered · literary · inviting",
    heading: "Georgia",
    body: "DM Sans",
    headingSize: "26px",
    bodySize: "14px",
    headingStyle: "'Georgia',serif",
    bodyStyle: "'DM Sans',sans-serif",
    googleFonts: "DM+Sans:wght@400;500",
    sample: { h: "Something worth making.", b: "Ideas become interfaces when you know how to ask the right question of the right material." },
    promptLine: "Type: Georgia headings 32px · DM Sans body 15px",
  },
  {
    id: "elegant-clear",
    name: "Elegant & Clear",
    mood: "Refined · editorial · high contrast",
    heading: "Playfair Display",
    body: "Lato",
    headingSize: "24px",
    bodySize: "14px",
    headingStyle: "'Playfair Display',serif",
    bodyStyle: "'Lato',sans-serif",
    googleFonts: "Playfair+Display:ital,wght@0,400;1,400&family=Lato:wght@400;700",
    sample: { h: "The prompt is a design decision.", b: "Before you build, know who you're building for and what they feel when they arrive." },
    promptLine: "Type: Playfair Display headings 30px (italic) · Lato body 15px",
  },
  {
    id: "modern-studio",
    name: "Modern Studio",
    mood: "Confident · minimal · structured",
    heading: "DM Serif Display",
    body: "Work Sans",
    headingSize: "26px",
    bodySize: "14px",
    headingStyle: "'DM Serif Display',serif",
    bodyStyle: "'Work Sans',sans-serif",
    googleFonts: "DM+Serif+Display:ital@0;1&family=Work+Sans:wght@400;500",
    sample: { h: "Built to last.", b: "A design system gives Claude a named visual language to be faithful to across every prompt in the session." },
    promptLine: "Type: DM Serif Display headings 32px · Work Sans body 15px medium",
  },
  {
    id: "soft-approachable",
    name: "Soft & Approachable",
    mood: "Friendly · warm · open",
    heading: "Lora",
    body: "Nunito",
    headingSize: "25px",
    bodySize: "14px",
    headingStyle: "'Lora',serif",
    bodyStyle: "'Nunito',sans-serif",
    googleFonts: "Lora:ital,wght@0,400;1,400&family=Nunito:wght@400;600",
    sample: { h: "Welcome. You belong here.", b: "The best interfaces feel like they were made for you specifically — because someone thought about you first." },
    promptLine: "Type: Lora headings 30px · Nunito body 16px",
  },
  {
    id: "bold-structured",
    name: "Bold & Structured",
    mood: "Decisive · typographic · graphic",
    heading: "Fraunces",
    body: "Libre Franklin",
    headingSize: "26px",
    bodySize: "14px",
    headingStyle: "'Fraunces',serif",
    bodyStyle: "'Libre Franklin',sans-serif",
    googleFonts: "Fraunces:ital,opsz,wght@0,9..144,400;1,9..144,400&family=Libre+Franklin:wght@400;500",
    sample: { h: "Six layers. Every time.", b: "Scope creep in the prompt creates scope creep in the code. One component per session, one direction per prompt." },
    promptLine: "Type: Fraunces headings 32px · Libre Franklin body 15px",
  },
  {
    id: "premium-refined",
    name: "Premium & Refined",
    mood: "Luxe · airy · precise",
    heading: "Cormorant Garamond",
    body: "Jost",
    headingSize: "28px",
    bodySize: "13px",
    headingStyle: "'Cormorant Garamond',serif",
    bodyStyle: "'Jost',sans-serif",
    googleFonts: "Cormorant+Garamond:ital,wght@0,400;1,400&family=Jost:wght@300;400;500",
    sample: { h: "Designed, not generated.", b: "Give Claude a named visual language and it will hold the line. Without it, every new prompt starts over." },
    promptLine: "Type: Cormorant Garamond headings 34px · Jost body 14px light",
  },
];

// ─── SHARED UI ────────────────────────────────────────────────
const Btn = ({ children, onClick, variant = "primary", style = {}, disabled }) => {
  const base = {
    fontFamily: sans,
    fontSize: type.ui,
    fontWeight: 600,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    borderRadius: 8,
    padding: "14px 28px",
    minHeight: 48, // WCAG 2.1 AA touch target minimum
    transition: "all 0.2s ease",
    letterSpacing: "0.01em",
    opacity: disabled ? 0.5 : 1,
    lineHeight: type.uiLh,
    ...style
  };

  const variants = {
    primary: { background: T.terra, color: T.white },
    ghost:   { background: "rgba(255,255,255,0.06)", color: T.white, border: `1px solid ${T.rule}` },
    cream:   { background: T.cream, color: T.ink },
    moss:    { background: T.moss, color: T.white },
  };

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ ...base, ...variants[variant] }}
      onMouseOver={(e) => !disabled && (e.currentTarget.style.opacity = "0.9")}
      onMouseOut={(e) => !disabled && (e.currentTarget.style.opacity = "1")}
      onFocus={(e) => !disabled && (e.currentTarget.style.outline = `3px solid ${variant === 'cream' ? T.terra : 'rgba(255,255,255,0.5)'}`, e.currentTarget.style.outlineOffset = "2px")}
      onBlur={(e) => (e.currentTarget.style.outline = "none")}
    >
      {children}
    </button>
  );
};

const Textarea = ({ value, onChange, placeholder, rows = 4, style = {}, ariaLabel }) => (
  <textarea
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    aria-label={ariaLabel || placeholder}
    style={{
      width: "100%",
      fontFamily: serif,
      fontSize: type.bodySm,
      lineHeight: type.bodySmLh,
      color: T.ink,
      background: T.white,
      border: "1.5px solid #dedad0",
      borderRadius: 10,
      padding: "18px 20px",
      resize: "vertical",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      ...style
    }}
    onFocus={(e) => {
      e.currentTarget.style.borderColor = "#c49a2a";
      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(196,154,42,0.15)";
    }}
    onBlur={(e) => {
      e.currentTarget.style.borderColor = "#dedad0";
      e.currentTarget.style.boxShadow = "none";
    }}
  />
);

// ─── GATE CHECK ───────────────────────────────────────────────
function GateCheck({ children }) {
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  function check() {
    if (code.trim() === GATE_CODE) {
      setUnlocked(true);
      localStorage.setItem("ws:gate_unlocked", "1");
    }
  }

  useEffect(() => {
    if (!GATE_CODE || localStorage.getItem("ws:gate_unlocked") === "1") {
      setUnlocked(true);
    }
  }, []);

  if (!GATE_CODE || unlocked) return children;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px" }}>
      <div style={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
        <h1 style={{ fontFamily: serif, fontSize: type.h2, color: T.cream, margin: "0 0 32px" }}>Access Required</h1>
        <input value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === "Enter" && check()}
          placeholder="Enter access code" type="password"
          style={{ width: "100%", fontFamily: sans, fontSize: type.body, color: T.cream, background: "rgba(255,255,255,0.05)", border: `1px solid ${T.rule}`, borderRadius: 8, padding: "14px 16px", outline: "none", textAlign: "center", marginBottom: 16, boxSizing: "border-box" }}
        />
        <Btn onClick={check} style={{ width: "100%", padding: "14px" }}>Continue</Btn>
      </div>
    </div>
  );
}

// ─── ENTRY SCREEN ─────────────────────────────────────────────
function EntryScreen({ onJoin, onFacilitator }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function go() {
    if (!name.trim()) { setErr("Please enter your name."); return; }
    if (!code.trim()) { setErr("Enter the session code."); return; }
    setLoading(true); setErr("");

    const upperCode = code.trim().toUpperCase();

    if (upperCode === FACILITATOR_CODE) {
      await setSessionCode("AZUL-SESSION");
      onFacilitator(name.trim() || "Azul");
      setLoading(false);
      return;
    }

    if (REVOKED_CODES.includes(upperCode)) {
      setErr("This session is closed. Please contact your facilitator.");
      setLoading(false);
      return;
    }

    // Check if session is expired (only if it exists, otherwise allow entry)
    try {
      const { data, error } = await supabase
        .from("sessions")
        .select("expires_at")
        .eq("code", upperCode)
        .maybeSingle();

      // Only check expiration if session exists and has an expiration date
      if (data && data.expires_at) {
        const expirationDate = new Date(data.expires_at);
        const now = new Date();
        if (now > expirationDate) {
          setErr("This session has expired. Please contact your facilitator.");
          setLoading(false);
          return;
        }
      }

      // If there's any error, just log it and continue (don't block entry)
      if (error) {
        console.warn("Session check warning:", error);
      }
    } catch (e) {
      // Log error but don't block entry
      console.warn("Session check warning:", e);
    }

    await setSessionCode(upperCode);
    await saveParticipantData(name.trim(), "joined_at", new Date().toISOString());
    onJoin(name.trim());
    setLoading(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0, padding: "48px 24px" }}>
      <div style={{ maxWidth: 480, width: "100%", textAlign: "center" }}>
        <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.2em", color: T.white, marginBottom: 32, textTransform: "uppercase" }}>Wonderpath Studio</div>
        <h1 style={{ fontFamily: serif, fontSize: 52, fontWeight: 400, color: T.cream, margin: "0 0 8px", lineHeight: 1.1 }}>Designing<br /><em style={{ color: T.terra }}>with AI</em></h1>
        <p style={{ fontFamily: sans, fontSize: type.ui, color: T.white, margin: "20px 0 48px", lineHeight: 1.6 }}>You are the director. AI is the builder.<br />Design thinking is your advantage.</p>

        <div style={{ background: T.bgMid, borderRadius: 16, padding: "40px", marginBottom: 24 }}>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.white, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>Your first name</label>
            <input value={name} onChange={e => { setName(e.target.value); setErr(""); }} onKeyDown={e => e.key === "Enter" && go()}
              placeholder="First name"
              style={{ width: "100%", fontFamily: serif, fontSize: 18, color: T.cream, background: "rgba(255,255,255,0.05)", border: `1px solid ${T.rule}`, borderRadius: 8, outline: "none", padding: "12px 16px", textAlign: "center", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.white, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>Session code</label>
            <input value={code} onChange={e => { setCode(e.target.value.toUpperCase()); setErr(""); }} onKeyDown={e => e.key === "Enter" && go()}
              placeholder="From facilitator"
              style={{ width: "100%", fontFamily: serif, fontSize: 18, color: T.cream, background: "rgba(255,255,255,0.05)", border: `1px solid ${T.rule}`, borderRadius: 8, outline: "none", padding: "12px 16px", textAlign: "center", letterSpacing: "0.1em", boxSizing: "border-box" }}
            />
          </div>

          {err && <div style={{ fontFamily: sans, fontSize: type.ui, color: T.terra, marginBottom: 16, textAlign: "center" }}>{err}</div>}

          <Btn onClick={go} disabled={loading} style={{ width: "100%", padding: "14px", fontSize: type.bodySm }}>
            {loading ? "Checking..." : "Join the session →"}
          </Btn>
        </div>

        <div style={{ fontFamily: sans, fontSize: type.label, color: T.white, lineHeight: 1.7, textAlign: "center" }}>
          By entering this session you agree that your work may be reviewed by the facilitator to support your learning and improve future workshops.
        </div>
      </div>
    </div>
  );
}

// ─── STEP NAVIGATOR ───────────────────────────────────────────
const STEPS = [
  { id: "intention", label: "Intention" },
  { id: "anatomy",   label: "Prompt anatomy" },
  // { id: "build",     label: "Build" }, // Merged into anatomy/preview
  { id: "reflect",   label: "Reflect" },
];

function StepNav({ current, name, onStepChange, userApiConfig, onRequestApiSetup }) {
  const idx = STEPS.findIndex(s => s.id === current);
  const [hoveredIdx, setHoveredIdx] = React.useState(null);
  // eslint-disable-next-line no-unused-vars
  const [apiHovered, setApiHovered] = React.useState(false);

  // Determine API provider label and color
  const getApiLabel = () => {
    if (!userApiConfig || userApiConfig.provider === "facilitator") {
      return { label: "Facilitator AI", color: "rgba(255,255,255,0.4)" };
    }
    if (userApiConfig.provider === "anthropic") {
      return { label: "Your Anthropic", color: "#D4845A" };
    }
    if (userApiConfig.provider === "openai") {
      return { label: "Your OpenAI", color: "#7EB89A" };
    }
    return { label: "Unknown", color: "rgba(255,255,255,0.4)" };
  };

  // eslint-disable-next-line no-unused-vars
  const apiInfo = getApiLabel();

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: T.bg, borderBottom: `1px solid ${T.rule}`, padding: "20px 40px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontFamily: sans, fontSize: type.label, color: T.white, letterSpacing: "0.1em", fontWeight: 600 }}>WONDERPATH</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {STEPS.map((s, i) => {
          const isActive = i === idx;
          const isCompleted = i < idx;
          const isHovered = hoveredIdx === i;
          const isClickable = isActive || isCompleted;

          return (
            <div
              key={s.id}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              onClick={() => isClickable && onStepChange && onStepChange(s.id)}
              style={{
                background: isActive ? "rgba(255,255,255,0.1)" : isHovered && isClickable ? "rgba(255,255,255,0.05)" : "transparent",
                borderRadius: 8,
                padding: "8px 16px",
                transition: "all 0.2s",
                cursor: isClickable ? "pointer" : "default"
              }}
            >
              <div style={{
                fontFamily: sans,
                fontSize: 18,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: isActive ? T.white : isCompleted ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.35)",
                fontWeight: isActive ? 600 : 400,
                transition: "color 0.2s"
              }}>
                {isCompleted && !isActive ? "✓ " : ""}{s.label}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        {/* API Provider Indicator - Hidden: using "Open in Claude/ChatGPT" workflow instead */}
        {/* <div
          onClick={onRequestApiSetup}
          onMouseEnter={() => setApiHovered(true)}
          onMouseLeave={() => setApiHovered(false)}
          style={{
            fontFamily: sans,
            fontSize: 12,
            color: apiInfo.color,
            letterSpacing: "0.05em",
            padding: "6px 12px",
            borderRadius: 6,
            background: apiHovered ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${apiHovered ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.08)"}`,
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}
          title="Click to change API provider"
        >
          <span>⚡</span>
          <span>{apiInfo.label}</span>
        </div> */}
        <span style={{ fontFamily: serif, fontSize: type.ui, color: T.white, fontStyle: "italic" }}>{name}</span>
      </div>
    </div>
  );
}

// ─── INTENTION STEP ───────────────────────────────────────────
function IntentionStep({ name, onNext, initialData, loadDemoData, demoLoaded }) {
  const [what, setWhat] = useState(initialData?.what || "");
  const [who, setWho] = useState(initialData?.who || "");
  const [ai, setAi] = useState({ text: "", loading: false });

  // Update local state when initialData changes (from demo load)
  useEffect(() => {
    if (initialData?.what) setWhat(initialData.what);
    if (initialData?.who) setWho(initialData.who);
  }, [initialData]);

  async function getCoaching() {
    if (!what.trim()) return;
    setAi({ text: "", loading: true });
    const t = await callAI(
      `You are a design coach at a vibe coding workshop run by Azul Ceballos at Wonderpath Studio. A participant just shared their build intention. Give one sharp, specific observation about the design challenge they're stepping into — name something they haven't thought of yet. Then end with one question that will make their prompt sharper. Warm, direct, 70 words max. No bullets.`,
      `Building: ${what}\nFor: ${who || "not specified yet"}`
    );
    setAi({ text: t, loading: false });
  }

  function next() {
    const data = { what, who };
    saveParticipantData(name, "intention", data);
    onNext(data);
  }

  const hasBothFilled = what.trim() && who.trim();

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.15em", color: T.terra, marginBottom: 16, textTransform: "uppercase" }}>Step 01</div>
      <h2 style={{ fontFamily: serif, fontSize: type.h1, fontWeight: 400, color: T.cream, margin: "0 0 12px" }}>What are you building?</h2>
      <p style={{ fontFamily: sans, fontSize: type.ui, color: T.white, margin: "0 0 24px", lineHeight: 1.6 }}>Before the first prompt, know your intention.</p>

      {/* Demo Loader */}
      <div style={{ background: "rgba(196,154,42,0.08)", border: "1.5px solid rgba(196,154,42,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 32, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: T.terra, marginBottom: 4 }}>
            {demoLoaded ? "Demo example loaded" : "See a complete example"}
          </div>
          <div style={{ fontFamily: sans, fontSize: 14, color: T.white, opacity: 0.8 }}>
            {demoLoaded
              ? "GreenSeason plant delivery — Click reload to reset"
              : hasBothFilled
                ? "Replace with GreenSeason demo (fills all steps)"
                : "Load GreenSeason demo (fills intention + all 6 layers)"}
          </div>
        </div>
        <button
          onClick={loadDemoData}
          style={{
            fontFamily: sans,
            fontSize: 14,
            fontWeight: 600,
            padding: "8px 16px",
            borderRadius: 8,
            border: "1.5px solid #c49a2a",
            background: demoLoaded ? "#c49a2a" : "transparent",
            color: demoLoaded ? "#ffffff" : "#c49a2a",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 0.15s"
          }}
        >
          {demoLoaded ? "Reload demo" : hasBothFilled ? "Overwrite with demo" : "Load demo"}
        </button>
      </div>

      <div style={{ background: T.cream, borderRadius: 16, padding: "36px 40px", display: "flex", flexDirection: "column", gap: 28 }}>
        <div>
          <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>What are you building?</label>
          <Textarea value={what} onChange={setWhat} placeholder="A workshop registration form, a personal habit tracker, a tool for..." rows={3} />
        </div>
        <div>
          <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>Who is it for?</label>
          <Textarea value={who} onChange={setWho} placeholder="Who specifically? What do they need that they don't have yet?" rows={3} />
        </div>
        {!ai.text && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="cream" onClick={getCoaching} disabled={!what.trim()}>{ai.loading ? "Thinking…" : "Get a coach note →"}</Btn>
          </div>
        )}
        {ai.text && (
          <div style={{ background: T.bgSurf, borderRadius: 12, padding: "28px 32px" }}>
            <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", color: T.terra, marginBottom: 12, textTransform: "uppercase" }}>Coach note</div>
            <p style={{ fontFamily: serif, fontSize: type.bodySm, color: T.cream, lineHeight: 1.75, margin: "0 0 20px" }}>{ai.text}</p>
            <button
              onClick={() => {
                setWhat(prev => prev.trim() ? `${prev}\n\n[Coach input]\n${ai.text}` : ai.text);
                setAi({ text: "", loading: false });
              }}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                fontWeight: 600,
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: T.terra,
                color: T.white,
                cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              ✓ Integrate coach input to my work
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 32 }}>
        <Btn onClick={next} disabled={!what.trim()}>Build my prompt →</Btn>
      </div>
    </div>
  );
}

// ─── COLOR PICKER ─────────────────────────────────────────────
function ColorPicker({ onSelect, selected }) {
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({
    c1name: "", c1hex: "",
    c2name: "", c2hex: "",
    c3name: "", c3hex: ""
  });

  function createCustomPalette() {
    const { c1name, c1hex, c2name, c2hex, c3name, c3hex } = custom;
    if (!c1name || !c1hex || !c2name || !c2hex || !c3name || !c3hex) return;
    const customPalette = {
      id: "custom",
      name: "Custom",
      mood: "Your palette",
      colors: [
        { name: c1name, hex: c1hex },
        { name: c2name, hex: c2hex },
        { name: c3name, hex: c3hex }
      ],
      promptLine: `Colors: ${c1name} ${c1hex} · ${c2name} ${c2hex} · ${c3name} ${c3hex}`
    };
    onSelect(customPalette);
    setShowCustom(false);
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: "#534ab7", textTransform: "uppercase" }}>
          Choose a color palette
        </div>
        <button onClick={() => setShowCustom(!showCustom)} style={{ background: "transparent", border: "none", fontFamily: sans, fontSize: type.label, color: "#534ab7", cursor: "pointer", textDecoration: "underline" }}>
          {showCustom ? "← Back to presets" : "+ Create custom"}
        </button>
      </div>

      {showCustom ? (
        <div style={{ background: "#f0f0fc", borderRadius: 12, padding: "20px 24px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input
                  type="text"
                  placeholder={`Color ${i} name`}
                  value={custom[`c${i}name`]}
                  onChange={e => setCustom({ ...custom, [`c${i}name`]: e.target.value })}
                  style={{ fontFamily: sans, fontSize: 14, padding: "8px 12px", borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.12)", outline: "none" }}
                />
                <input
                  type="text"
                  placeholder="#000000"
                  value={custom[`c${i}hex`]}
                  onChange={e => setCustom({ ...custom, [`c${i}hex`]: e.target.value })}
                  style={{ fontFamily: sans, fontSize: 14, padding: "8px 12px", borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.12)", outline: "none" }}
                />
              </div>
            ))}
          </div>
          <button
            onClick={createCustomPalette}
            disabled={!custom.c1name || !custom.c1hex || !custom.c2name || !custom.c2hex || !custom.c3name || !custom.c3hex}
            style={{
              fontFamily: sans,
              fontSize: 14,
              fontWeight: 600,
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background: "#534ab7",
              color: "white",
              cursor: "pointer",
              opacity: (!custom.c1name || !custom.c1hex || !custom.c2name || !custom.c2hex || !custom.c3name || !custom.c3hex) ? 0.4 : 1
            }}
          >
            ✓ Use this palette
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {COLOR_PALETTES.map(p => {
            const isSel = selected === p.id;
            return (
              <button key={p.id} onClick={() => onSelect(p)}
                style={{ textAlign: "left", cursor: "pointer", borderRadius: 10, padding: "12px 14px", border: isSel ? "2px solid #534ab7" : "1.5px solid rgba(0,0,0,0.1)", background: isSel ? "#f0f0fc" : "rgba(255,255,255,0.7)", outline: "none", transition: "all 0.15s" }}>
                <div style={{ display: "flex", gap: 5, marginBottom: 9 }}>
                  {p.colors.map(c => (
                    <div key={c.name} title={`${c.name} ${c.hex}`}
                      style={{ flex: 1, height: 28, borderRadius: 5, background: c.hex, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} />
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {p.colors.map(c => (
                    <div key={c.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: isSel ? "#534ab7" : "#3d4038" }}>{c.name}</span>
                      <span style={{ fontFamily: sans, fontSize: type.label, color: "#9a9d98" }}>{c.hex}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontFamily: sans, fontSize: type.label, color: "#9a9d98", fontStyle: "italic" }}>{p.mood}</div>
              </button>
            );
          })}
        </div>
      )}
      {selected && !showCustom && (
        <div style={{ marginTop: 10, background: "#eeedfe", borderRadius: 8, padding: "10px 14px" }}>
          <span style={{ fontFamily: sans, fontSize: type.label, color: "#534ab7" }}>
            ✓ {COLOR_PALETTES.find(p => p.id === selected)?.promptLine || (typeof selected === 'object' ? selected.promptLine : '')}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── FONT PICKER ──────────────────────────────────────────────
function FontPicker({ onSelect, selected }) {
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({
    heading: "",
    headingSize: "",
    body: "",
    bodySize: ""
  });

  // Load all Google Fonts once
  if (!fontsLoaded) {
    const allFonts = FONT_PAIRS.map(p => p.googleFonts).join("&family=");
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${allFonts}&display=swap`;
    document.head.appendChild(link);
    setFontsLoaded(true);
  }

  function createCustomFonts() {
    const { heading, headingSize, body, bodySize } = custom;
    if (!heading || !headingSize || !body || !bodySize) return;
    const customPair = {
      id: "custom",
      name: "Custom",
      mood: "Your type pairing",
      heading,
      body,
      headingSize,
      bodySize,
      headingStyle: heading,
      bodyStyle: body,
      promptLine: `Type: ${heading} headings ${headingSize} · ${body} body ${bodySize}`,
      sample: { h: "Custom Heading", b: "Custom body text in your selected typeface." }
    };
    onSelect(customPair);
    setShowCustom(false);
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: "#534ab7", textTransform: "uppercase" }}>
          Choose a type pairing
        </div>
        <button onClick={() => setShowCustom(!showCustom)} style={{ background: "transparent", border: "none", fontFamily: sans, fontSize: type.label, color: "#534ab7", cursor: "pointer", textDecoration: "underline" }}>
          {showCustom ? "← Back to presets" : "+ Create custom"}
        </button>
      </div>

      {showCustom ? (
        <div style={{ background: "#f0f0fc", borderRadius: 12, padding: "20px 24px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: sans, fontSize: type.label, color: "#534ab7", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Heading font</div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
                <input
                  type="text"
                  placeholder="Georgia, Arial, etc."
                  value={custom.heading}
                  onChange={e => setCustom({ ...custom, heading: e.target.value })}
                  style={{ fontFamily: sans, fontSize: 14, padding: "8px 12px", borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.12)", outline: "none" }}
                />
                <input
                  type="text"
                  placeholder="32px"
                  value={custom.headingSize}
                  onChange={e => setCustom({ ...custom, headingSize: e.target.value })}
                  style={{ fontFamily: sans, fontSize: 14, padding: "8px 12px", borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.12)", outline: "none" }}
                />
              </div>
            </div>
            <div>
              <div style={{ fontFamily: sans, fontSize: type.label, color: "#534ab7", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Body font</div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
                <input
                  type="text"
                  placeholder="Helvetica, Times, etc."
                  value={custom.body}
                  onChange={e => setCustom({ ...custom, body: e.target.value })}
                  style={{ fontFamily: sans, fontSize: 14, padding: "8px 12px", borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.12)", outline: "none" }}
                />
                <input
                  type="text"
                  placeholder="15px"
                  value={custom.bodySize}
                  onChange={e => setCustom({ ...custom, bodySize: e.target.value })}
                  style={{ fontFamily: sans, fontSize: 14, padding: "8px 12px", borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.12)", outline: "none" }}
                />
              </div>
            </div>
          </div>
          <button
            onClick={createCustomFonts}
            disabled={!custom.heading || !custom.headingSize || !custom.body || !custom.bodySize}
            style={{
              fontFamily: sans,
              fontSize: 14,
              fontWeight: 600,
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background: "#534ab7",
              color: "white",
              cursor: "pointer",
              opacity: (!custom.heading || !custom.headingSize || !custom.body || !custom.bodySize) ? 0.4 : 1
            }}
          >
            ✓ Use this pairing
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {FONT_PAIRS.map(pair => {
            const isSelected = selected === pair.id;
            return (
              <button key={pair.id} onClick={() => onSelect(pair)}
                style={{
                  textAlign: "left", cursor: "pointer", borderRadius: 12, padding: "16px 18px",
                  border: isSelected ? `2px solid #534ab7` : "1.5px solid rgba(0,0,0,0.1)",
                  background: isSelected ? "#f0f0fc" : "rgba(255,255,255,0.7)",
                  transition: "all 0.15s", outline: "none",
                }}>
                <div style={{ fontFamily: pair.headingStyle, fontSize: pair.headingSize, color: "#1a1c18", lineHeight: 1.2, marginBottom: 6 }}>
                  {pair.sample.h}
                </div>
                <div style={{ fontFamily: pair.bodyStyle, fontSize: pair.bodySize, color: "#6a6d62", lineHeight: 1.55, marginBottom: 10 }}>
                  {pair.sample.b}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, color: isSelected ? "#534ab7" : "#9a9d98", letterSpacing: "0.06em" }}>
                    {pair.name}
                  </span>
                  <span style={{ fontFamily: sans, fontSize: type.label, color: "#b0b3a8", fontStyle: "italic" }}>
                    {pair.mood}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {selected && !showCustom && (
        <div style={{ marginTop: 10, background: "#eeedfe", borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: sans, fontSize: type.label, color: "#534ab7" }}>
            ✓ {FONT_PAIRS.find(p => p.id === selected)?.promptLine || (typeof selected === 'object' ? selected.promptLine : '')}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── ANATOMY STEP ─────────────────────────────────────────────
function AnatomyStep({ name, onNext, initialData, loadDemoData, demoLoaded, userApiConfig, onRequestApiSetup }) {
  const [layerIdx, setLayerIdx] = useState(0);
  const [fields, setFields] = useState(initialData || { context: "", references: "", direction: "", system: "", scope: "", success: "" });
  const [showExample, setShowExample] = useState(false);
  const [preview, setPreview] = useState(false);
  const [ai, setAi] = useState({ text: "", loading: false });

  // Helper to call AI with user's config
  const callUserAI = async (system, user, maxTokens = 900) => {
    // Show API setup modal if user hasn't configured their key yet
    if (!userApiConfig) {
      onRequestApiSetup();
      return "Please set up your API key to continue.";
    }
    return await callAI(system, user, maxTokens, userApiConfig);
  };
  const [copied, setCopied] = useState(false);
  const [copiedForClaude, setCopiedForClaude] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [copiedForChatGPT, setCopiedForChatGPT] = useState(false);
  const [selectedFont, setSelectedFont] = useState(null);
  const [selfAssessment, setSelfAssessment] = useState(null);
  const [assessingMe, setAssessingMe] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [experienceTone, setExperienceTone] = useState("");
  const [showCustomPriority, setShowCustomPriority] = useState(false);
  const [customPriority, setCustomPriority] = useState("");
  const [referenceUrls, setReferenceUrls] = useState([
    { url: "", notes: "", relevant: false },
    { url: "", notes: "", relevant: false },
    { url: "", notes: "", relevant: false }
  ]);
  const [referenceAi, setReferenceAi] = useState({ text: "", loading: false });
  const [designAssistantOpen, setDesignAssistantOpen] = useState(false);
  const [designAssistantInput, setDesignAssistantInput] = useState("");
  const [designAssistantImage, setDesignAssistantImage] = useState(null);
  const [designAssistantResearchMode, setDesignAssistantResearchMode] = useState(false);
  const [designAssistantAi, setDesignAssistantAi] = useState({ r: "", l: false });
  const [systemPrinciples, setSystemPrinciples] = useState({ text: "", loading: false });
  const [systemQuestion, setSystemQuestion] = useState("");
  const [systemAnswer, setSystemAnswer] = useState({ text: "", loading: false });
  const [systemTopicView, setSystemTopicView] = useState(null); // { topic: "...", content: "", loading: false }
  const [fontScale, setFontScale] = useState("comfortable");
  const [spacing, setSpacing] = useState("airy");
  const [cornerStyle, setCornerStyle] = useState("subtle");
  const [componentStyle, setComponentStyle] = useState("minimal");
  const [specsGuidance, setSpecsGuidance] = useState({ text: "", loading: false });
  const [brandPositioning, setBrandPositioning] = useState({ text: "", loading: false });
  const [visualAssets, setVisualAssets] = useState({ text: "", loading: false });
  const [enhancedPrompt, setEnhancedPrompt] = useState({ text: "", loading: false });
  const [customDesignSystem, setCustomDesignSystem] = useState({ text: "", loading: false });
  const [enhancementProgress, setEnhancementProgress] = useState({ step: 0, total: 0, currentStep: "", active: false });
  const [brandPositioningCopied, setBrandPositioningCopied] = useState(false);
  const [visualAssetsCopied, setVisualAssetsCopied] = useState(false);
  const [customSystemCopied, setCustomSystemCopied] = useState(false);
  const [coachingPromptCopied, setCoachingPromptCopied] = useState(false);
  const [enhancementPromptCopied, setEnhancementPromptCopied] = useState(false);

  const layer = LAYERS[layerIdx];
  const lStyle = L[layer.key];
  const allFilled = LAYERS.every(l => {
    if (l.combined) {
      return fields.scope.trim() && fields.success.trim();
    }
    return fields[l.key].trim();
  });
  const currentFilled = layer.combined ? (fields.scope.trim() && fields.success.trim()) : fields[layer.key].trim();

  function handleFontSelect(pair) {
    setSelectedFont(pair.id);
    setFields(f => {
      const existing = f.system;
      const typeLine = pair.promptLine;
      if (/^Type:/m.test(existing)) return { ...f, system: existing.replace(/^Type:.*$/m, typeLine) };
      return { ...f, system: existing ? `${existing}\n${typeLine}` : typeLine };
    });
  }

  function handleColorSelect(palette) {
    setSelectedColor(palette.id);
    setFields(f => {
      const existing = f.system;
      const colorLine = palette.promptLine;
      if (/^Colors:/m.test(existing)) return { ...f, system: existing.replace(/^Colors:.*$/m, colorLine) };
      return { ...f, system: existing ? `${colorLine}\n${existing}` : colorLine };
    });
  }

  function handlePriorityToggle(priority) {
    setSelectedPriorities(prev => {
      const isSelected = prev.includes(priority);
      let newPriorities;
      if (isSelected) {
        newPriorities = prev.filter(p => p !== priority);
      } else if (prev.length < 3) {
        newPriorities = [...prev, priority];
      } else {
        return prev;
      }
      updateDirectionField(newPriorities, experienceTone);
      return newPriorities;
    });
  }

  function handleToneChange(tone) {
    setExperienceTone(tone);
    updateDirectionField(selectedPriorities, tone);
  }

  function addCustomPriority() {
    if (!customPriority.trim()) return;
    if (selectedPriorities.length >= 3) return;
    const newPriorities = [...selectedPriorities, customPriority.trim()];
    setSelectedPriorities(newPriorities);
    updateDirectionField(newPriorities, experienceTone);
    setCustomPriority("");
    setShowCustomPriority(false);
  }

  function updateDirectionField(priorities, tone) {
    setFields(f => {
      let existing = f.direction;
      // Remove old priorities and tone lines
      existing = existing.replace(/^Key design priorities:.*$/m, "").replace(/^Experience tone:.*$/m, "").trim();

      let additions = "";
      if (priorities.length > 0) {
        additions += `Key design priorities: ${priorities.join(", ")}\n`;
      }
      if (tone.trim()) {
        additions += `Experience tone: ${tone}\n`;
      }

      return { ...f, direction: additions + (existing ? "\n" + existing : "") };
    });
  }

  function updateReferenceUrl(index, field, value) {
    setReferenceUrls(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      updateReferencesField(updated);
      return updated;
    });
  }

  function updateReferencesField(refs) {
    setFields(f => {
      let content = "";
      refs.forEach((ref, i) => {
        if (ref.url.trim()) {
          content += `Reference ${i + 1}: ${ref.url}\n`;
          if (ref.notes.trim()) {
            content += `What stands out: ${ref.notes}\n`;
          }
          if (ref.relevant) {
            content += `✓ Relevant to my product\n`;
          }
          content += `\n`;
        }
      });
      return { ...f, references: content.trim() };
    });
  }

  function loadExampleReferences() {
    const examples = [
      { url: "https://www.apple.com", notes: "Simplicity, visual hierarchy, whitespace", relevant: false },
      { url: "https://www.nike.com", notes: "Bold imagery, clear CTAs, energy", relevant: false },
      { url: "https://www.airbnb.com", notes: "Trust signals, user-generated content, clarity", relevant: true }
    ];
    setReferenceUrls(examples);
    updateReferencesField(examples);
  }

  async function getAiInterpretation() {
    const filledRefs = referenceUrls.filter(r => r.url.trim());
    if (filledRefs.length === 0) return;

    setReferenceAi({ text: "", loading: true });
    const refsText = filledRefs.map((r, i) =>
      `Reference ${i + 1}: ${r.url}\n${r.notes ? `What stands out: ${r.notes}` : ""}\n${r.relevant ? "✓ Relevant to their product" : ""}`
    ).join("\n\n");

    const t = await callAI(
      `You are a design coach helping a workshop participant learn from quality design examples. They've shared ${filledRefs.length} reference${filledRefs.length > 1 ? 's' : ''}. For each, identify one specific design principle at work (not just aesthetics — structure, hierarchy, psychology). Then give one insight about how these principles could apply to their context. Warm, specific, no fluff. Max 120 words.`,
      `Participant: ${name}\n\nReferences:\n${refsText}\n\nTheir product context: ${fields.context || "Not yet defined"}`
    );
    setReferenceAi({ text: t, loading: false });
  }

  async function openInClaude() {
    // Assemble full context for Claude
    const context = `I'm ${name}, working through a design workshop. Here's my six-layer prompt for a product I'm building:

${assemblePrompt()}

${brandPositioning.text ? `\n━━━ BRAND POSITIONING INSIGHTS ━━━\n${brandPositioning.text}\n` : ""}${visualAssets.text ? `\n━━━ VISUAL ASSETS RECOMMENDATIONS ━━━\n${visualAssets.text}\n` : ""}${customDesignSystem.text ? `\n━━━ CUSTOM DESIGN SYSTEM ━━━\n${customDesignSystem.text}\n` : ""}${ai.text ? `\n━━━ COACHING FEEDBACK ━━━\n${ai.text}\n` : ""}${selfAssessment ? `\n━━━ AI ASSESSMENT ━━━\nStatus: ${selfAssessment.status}\n${selfAssessment.analysis}\n` : ""}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**What I need help with:**

I'd like you to help me in one or more of these ways:

1. **Generate an HTML/CSS mockup** — Create a complete, working HTML file with inline CSS based on this prompt. Make it responsive, accessible, and visually distinctive (not generic). Use the design system colors, fonts, and principles I specified.

2. **Refine my prompt** — Identify where I'm being too vague or generic. Suggest specific improvements to make my direction clearer and more distinctive. Point out any contradictions or missing details.

3. **Design feedback** — Review my design choices (colors, fonts, visual direction, priorities) and tell me what's working and what could be stronger. Help me avoid generic solutions.

4. **Expand my design system** — Take my design system layer and expand it into a complete system with component patterns, interaction states, responsive breakpoints, and accessibility guidelines.

5. **Visual inspiration** — Based on my direction and references, suggest 3-5 specific design patterns, color palettes, or UI approaches I should consider.

Which would be most helpful to start with? Or feel free to tackle multiple at once.`;

    try {
      await navigator.clipboard.writeText(context);
      setCopiedForClaude(true);
      setTimeout(() => setCopiedForClaude(false), 3000);

      // Open Claude.ai in new tab
      window.open('https://claude.ai/new', '_blank');
    } catch (err) {
      alert('Failed to copy to clipboard. Please try again.');
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function openInChatGPT() {
    // Assemble full context for ChatGPT
    const context = `I'm ${name}, working through a design workshop. Here's my six-layer prompt for a product I'm building:

${assemblePrompt()}

${brandPositioning.text ? `\n━━━ BRAND POSITIONING INSIGHTS ━━━\n${brandPositioning.text}\n` : ""}${visualAssets.text ? `\n━━━ VISUAL ASSETS RECOMMENDATIONS ━━━\n${visualAssets.text}\n` : ""}${customDesignSystem.text ? `\n━━━ CUSTOM DESIGN SYSTEM ━━━\n${customDesignSystem.text}\n` : ""}${ai.text ? `\n━━━ COACHING FEEDBACK ━━━\n${ai.text}\n` : ""}${selfAssessment ? `\n━━━ AI ASSESSMENT ━━━\nStatus: ${selfAssessment.status}\n${selfAssessment.analysis}\n` : ""}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**What I need help with:**

I'd like you to help me in one or more of these ways:

1. **Generate an HTML/CSS mockup** — Create a complete, working HTML file with inline CSS based on this prompt. Make it responsive, accessible, and visually distinctive (not generic). Use the design system colors, fonts, and principles I specified.

2. **Refine my prompt** — Identify where I'm being too vague or generic. Suggest specific improvements to make my direction clearer and more distinctive. Point out any contradictions or missing details.

3. **Design feedback** — Review my design choices (colors, fonts, visual direction, priorities) and tell me what's working and what could be stronger. Help me avoid generic solutions.

4. **Expand my design system** — Take my design system layer and expand it into a complete system with component patterns, interaction states, responsive breakpoints, and accessibility guidelines.

5. **Visual inspiration** — Based on my direction and references, suggest 3-5 specific design patterns, color palettes, or UI approaches I should consider.

Which would be most helpful to start with? Or feel free to tackle multiple at once.`;

    try {
      await navigator.clipboard.writeText(context);
      setCopiedForChatGPT(true);
      setTimeout(() => setCopiedForChatGPT(false), 3000);

      // Open ChatGPT in new tab
      window.open('https://chatgpt.com/', '_blank');
    } catch (err) {
      alert('Failed to copy to clipboard. Please try again.');
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function getBrandPositioning() {
    const hasContext = fields.context && fields.context.trim().length > 50;
    if (!hasContext) {
      alert("Please complete the Context layer first before getting brand positioning guidance.");
      return;
    }

    setBrandPositioning({ text: "", loading: true });

    const promptText = `
PARTICIPANT: ${name}

PRODUCT CONTEXT:
${fields.context}

DESIGN PRIORITIES: ${selectedPriorities.length > 0 ? selectedPriorities.join(", ") : "Not yet defined"}
EXPERIENCE TONE: ${experienceTone || "Not yet defined"}

---

TASK: Research and analyze the VISUAL TONE blind spot in this product's brand space.

Most founders think about brand positioning in terms of messaging, but they miss the VISUAL TONE — the specific, distinctive design language that communicates their positioning before a word is read.

Your job:
1. **Identify the brand space** — What category/space does this product live in? What are the dominant visual patterns in that space? (Colors, type styles, imagery, component patterns)

2. **Spot the visual blind spot** — What visual territories are AVOIDED in this space that could actually serve their positioning? What visual language would feel distinctive but still credible?

3. **Define a visual north star** — Give them ONE specific visual direction that anchors their brand positioning. Not generic adjectives ("clean", "modern") — concrete visual strategy with rationale.

4. **Strategic recommendations** — 2-3 specific visual choices they should make to differentiate while staying credible. Think: color psychology, typography personality, spatial rhythm, component philosophy.

Keep it specific, actionable, and grounded in their actual product context. Max 200 words.

FORMAT:
**Brand Space:** [category + dominant visual patterns]
**Visual Blind Spot:** [untapped territory]
**Visual North Star:** [one distinctive direction]
**Strategic Moves:** [2-3 specific recommendations]
`;

    try {
      const response = await callUserAI(
        "You are a brand positioning and visual design strategist. You help founders discover distinctive visual territories that align with their strategic positioning. Be specific, avoid generic design terms, ground recommendations in psychology and semiotics.",
        promptText,
        300
      );
      setBrandPositioning({ text: response, loading: false });
    } catch (error) {
      setBrandPositioning({ text: "Error generating brand positioning guidance. Please try again.", loading: false });
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function getVisualAssets() {
    const hasContext = fields.context && fields.context.trim().length > 50;
    const hasDirection = fields.direction && fields.direction.trim().length > 30;

    if (!hasContext || !hasDirection) {
      alert("Please complete Context and Direction layers first before getting visual asset recommendations.");
      return;
    }

    setVisualAssets({ text: "", loading: true });

    const promptText = `
PARTICIPANT: ${name}

PRODUCT CONTEXT:
${fields.context}

DESIGN DIRECTION:
${fields.direction}

BRAND POSITIONING: ${brandPositioning.text || "Not yet defined"}

---

TASK: Recommend specific visual design assets this participant should commission from designers or artists to make their product distinctive and not generic.

Most AI-generated products feel samey because they rely on stock imagery, default icons, and generic visual language. Custom visual assets create brand recognition and emotional resonance.

Your job:
1. **Identify the asset types** — Based on their product and direction, which 2-3 types of visual assets would have the highest impact? (Icons, illustrations, animations, photography, 3D visuals, video, motion graphics, sound/music, textures, patterns, etc.)

2. **Provide rationale** — For each asset type, explain WHY it would be valuable specifically for their product. What problem does it solve? What feeling does it create? How does it differentiate?

3. **Art direction guidance** — Give concrete direction for how these assets should feel/look. Not vague ("professional, modern") but specific ("Isometric illustrations with warm shadows, showing human hands interacting with objects, muted earth tones, slightly imperfect line work to avoid sterile tech feel")

4. **Practical notes** — Mention when/where these assets would be used in the product and any technical considerations (file formats, responsiveness, performance, accessibility)

Keep it specific, actionable, and strategic. Max 250 words.

FORMAT:
**Recommended Assets:**
1. [Asset type]: [Rationale - why this matters]

**Art Direction:**
[Specific visual guidance for how these should look/feel]

**Usage & Technical Notes:**
[Where to use them, formats, considerations]
`;

    try {
      const response = await callUserAI(
        "You are a creative director and brand strategist helping founders commission distinctive visual assets. Be specific about visual style, avoid generic terms, prioritize impact and differentiation. Ground recommendations in their actual product needs.",
        promptText,
        350
      );
      setVisualAssets({ text: response, loading: false });
    } catch (error) {
      setVisualAssets({ text: "Error generating visual asset recommendations. Please try again.", loading: false });
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function generateCustomDesignSystem() {
    const hasContext = fields.context && fields.context.trim().length > 50;
    const hasReferences = fields.references && fields.references.trim().length > 30;
    const hasDirection = fields.direction && fields.direction.trim().length > 30;

    if (!hasContext || !hasReferences || !hasDirection) {
      alert("Please complete Context, Quality Design, and Direction layers first before generating a custom design system.");
      return;
    }

    setCustomDesignSystem({ text: "", loading: true });

    // Collect all the design system notes gathered so far
    const colorNote = selectedColor ? `Selected Color: ${selectedColor}` : "";
    const fontNote = selectedFont ? `Selected Font: ${typeof selectedFont === 'object' ? selectedFont.promptLine : selectedFont}` : "";
    const prioritiesNote = selectedPriorities.length > 0 ? `Key Priorities: ${selectedPriorities.join(", ")}` : "";
    const toneNote = experienceTone ? `Experience Tone: ${experienceTone}` : "";
    const specificationsNote = `Font Scale: ${fontScale}, Spacing: ${spacing}, Corner Style: ${cornerStyle}, Component Style: ${componentStyle}`;
    const systemFieldNote = fields.system ? `Current System Notes:\n${fields.system}` : "";

    const promptText = `
PARTICIPANT: ${name}

PRODUCT CONTEXT:
${fields.context}

QUALITY DESIGN REFERENCES:
${fields.references}

DESIGN DIRECTION:
${fields.direction}

COLLECTED DESIGN SYSTEM NOTES:
${colorNote}
${fontNote}
${prioritiesNote}
${toneNote}
${specificationsNote}
${systemFieldNote}

---

TASK: Create a comprehensive, custom design system and brand inspiration specifically tailored to this participant's product.

This design system should feel unique, intentional, and aligned with their vision. Avoid generic recommendations — ground everything in their specific context, references, and direction.

Your job:

1. **Custom Color Palette** — Based on their direction, references, and selected color (if any), recommend a complete palette:
   - Primary color (main brand color, with hex value)
   - Secondary color (supporting accent, with hex value)
   - Background colors (light/dark modes if relevant, with hex values)
   - Text colors (primary, secondary, tertiary hierarchy, with hex values and contrast ratios)
   - Status colors (success, warning, error, info — with hex values)
   - Explain WHY these colors work for their specific product and user

2. **Typography System** — Recommend specific font pairings (not just "sans-serif"):
   - Heading font (with fallback stack and link to Google Fonts if possible)
   - Body font (with fallback stack and link to Google Fonts if possible)
   - Complete type scale (H1 through body text, with px sizes, weights, line-heights)
   - When to use each size (with specific examples from their product)

3. **Spacing & Layout System** — Define their spacing scale and layout principles:
   - Base spacing unit (e.g. 4px, 8px)
   - Complete scale (xs through xxxl with values)
   - Layout grid structure (columns, gutters, margins for mobile/tablet/desktop)
   - Whitespace strategy based on their priorities

4. **Visual Language** — The details that make this design system distinctive:
   - Border radius values (subtle, standard, pill — based on their corner style)
   - Shadow system (elevation levels 1-4 with shadow values)
   - Animation principles (duration, easing, when to animate)
   - Illustration/iconography style (based on their references and direction)

5. **Brand Inspiration & Visual Mood** — Help them visualize the aesthetic:
   - 3-5 specific visual references that match their vibe (can be products, artworks, photography styles, design movements)
   - Color palette inspiration (e.g. "Think autumn forest floor meets Scandinavian ceramics")
   - Texture/material metaphors (e.g. "Warm linen, brushed steel, soft moss")
   - What this design system should NEVER feel like (what to avoid)

Keep it specific, actionable, and inspiring. Include hex values, font names, pixel values. Max 500 words.

FORMAT:
**Custom Color Palette:**
[Specific palette with hex values and rationale]

**Typography System:**
[Specific fonts with type scale and usage]

**Spacing & Layout:**
[Spacing scale and layout principles]

**Visual Language:**
[Border radius, shadows, animations, illustration style]

**Brand Inspiration & Visual Mood:**
[3-5 specific visual references, color mood, textures, what to avoid]
`;

    try {
      const response = await callUserAI(
        "You are a brand and design system strategist helping non-technical founders create distinctive, cohesive visual systems. Be specific about colors (use hex values), fonts (name actual typefaces), spacing (provide pixel values), and visual style. Avoid generic advice. Ground every recommendation in the participant's actual product needs and aesthetic direction.",
        promptText,
        750
      );
      setCustomDesignSystem({ text: response, loading: false });
    } catch (error) {
      setCustomDesignSystem({ text: "Error generating custom design system. Please try again.", loading: false });
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function enhancePromptForMockup() {
    // Ensure all layers are filled
    if (!allFilled) {
      alert("Please complete all six layers before enhancing your prompt for mockup generation.");
      return;
    }

    // Initialize progress tracking
    const totalSteps = 8;
    setEnhancementProgress({ step: 0, total: totalSteps, currentStep: "Starting enhancement process...", active: true });
    setEnhancedPrompt({ text: "", loading: true });

    try {
      // STEP 1: Gather ALL context (including AI-generated insights)
      setEnhancementProgress({ step: 1, total: totalSteps, currentStep: "Gathering all context and AI insights...", active: true });

      const allContext = `
SIX-LAYER PROMPT:
${assemblePrompt()}

AI-GENERATED INSIGHTS:
${brandPositioning.text ? `\n[Brand Positioning]\n${brandPositioning.text}` : ""}
${visualAssets.text ? `\n[Visual Assets to Commission]\n${visualAssets.text}` : ""}
${customDesignSystem.text ? `\n[Custom Design System]\n${customDesignSystem.text}` : ""}
${ai.text ? `\n[Coaching Feedback]\n${ai.text}` : ""}
${selfAssessment ? `\n[AI Assessment]\nStatus: ${selfAssessment.status}\nAnalysis: ${selfAssessment.analysis}` : ""}

DESIGN SELECTIONS:
- Selected Color: ${selectedColor || "Not selected"}
- Selected Font: ${selectedFont ? (typeof selectedFont === 'object' ? selectedFont.promptLine : selectedFont) : "Not selected"}
- Key Priorities: ${selectedPriorities.join(", ") || "Not selected"}
- Experience Tone: ${experienceTone || "Not specified"}
- Font Scale: ${fontScale}, Spacing: ${spacing}, Corners: ${cornerStyle}, Components: ${componentStyle}
`;

      // STEP 2: Enhance Context Layer
      setEnhancementProgress({ step: 2, total: totalSteps, currentStep: "Enhancing Context layer with specific scenarios...", active: true });
      const contextEnhanced = await callUserAI(
        "You are enhancing the CONTEXT layer for Claude mockup generation. Transform abstract user descriptions into concrete user scenarios, use cases, and technical requirements. Include specific device context, time constraints, emotional states, and what success looks like for this user. Be specific and technical.",
        `Original Context:\n${fields.context}\n\nAll Available Context:\n${allContext}\n\nTask: Enhance this context with specific user scenarios, device details, behavioral patterns, and technical constraints. Max 150 words.`,
        250
      );

      // STEP 3: Enhance Quality Design Layer
      setEnhancementProgress({ step: 3, total: totalSteps, currentStep: "Extracting design patterns from references...", active: true });
      const qualityEnhanced = await callUserAI(
        "You are enhancing the QUALITY DESIGN layer. Extract specific, actionable design patterns from the references provided. Identify visual properties, interaction patterns, layout strategies, and design principles. Be concrete - name specific techniques, not abstract qualities.",
        `Original Quality Design:\n${fields.references}\n\nAll Available Context:\n${allContext}\n\nTask: Extract specific design patterns, visual techniques, and principles to adopt or avoid. Max 150 words.`,
        250
      );

      // STEP 4: Enhance Direction Layer
      setEnhancementProgress({ step: 4, total: totalSteps, currentStep: "Converting direction into visual specifications...", active: true });
      const directionEnhanced = await callUserAI(
        "You are enhancing the DIRECTION layer. Convert abstract aesthetic terms into concrete visual specifications. Replace 'modern' with specific properties, 'friendly' with UI choices, 'professional' with layout decisions. Use the brand positioning and visual assets insights.",
        `Original Direction:\n${fields.direction}\n\nAll Available Context:\n${allContext}\n\nTask: Convert abstract direction into specific visual properties, UI choices, and aesthetic specifications. Include color psychology, typography voice, spatial rhythm. Max 200 words.`,
        300
      );

      // STEP 5: Enhance Design System Layer
      setEnhancementProgress({ step: 5, total: totalSteps, currentStep: "Completing design system specifications...", active: true });
      const systemEnhanced = await callUserAI(
        "You are enhancing the DESIGN SYSTEM layer. Provide complete technical specifications ready for implementation. Include exact hex values, font stacks, spacing values, border radius, shadow definitions, animation specs. Use the custom design system if available.",
        `Original Design System:\n${fields.system}\n\nAll Available Context:\n${allContext}\n\nTask: Provide complete design system with hex values, type scale (sizes/weights/line-heights), spacing scale (px values), border radius, shadows, component patterns. Max 250 words.`,
        400
      );

      // STEP 6: Enhance Scope Layer
      setEnhancementProgress({ step: 6, total: totalSteps, currentStep: "Adding technical implementation details...", active: true });
      const scopeEnhanced = await callUserAI(
        "You are enhancing the SCOPE layer. Add technical implementation guidance: HTML semantic structure, component hierarchy, accessibility attributes, responsive breakpoints, interaction states. Make it implementation-ready.",
        `Original Scope:\n${fields.scope}\n\nAll Available Context:\n${allContext}\n\nTask: Add HTML structure, component breakdown, accessibility requirements, responsive behavior, state management. Max 200 words.`,
        300
      );

      // STEP 7: Enhance Success Layer
      setEnhancementProgress({ step: 7, total: totalSteps, currentStep: "Defining measurable success criteria...", active: true });
      const successEnhanced = await callUserAI(
        "You are enhancing the SUCCESS layer. Transform abstract success into measurable, observable criteria. How will we know the mockup succeeded? What specific user experience signals matter?",
        `Original Success:\n${fields.success}\n\nAll Available Context:\n${allContext}\n\nTask: Define measurable success criteria, specific UX goals, observable user behaviors, and design validation checkpoints. Max 150 words.`,
        250
      );

      // STEP 8: Assemble and validate
      setEnhancementProgress({ step: 8, total: totalSteps, currentStep: "Assembling final enhanced prompt...", active: true });

      const finalEnhanced = `[CONTEXT - Enhanced]
${contextEnhanced}

[QUALITY DESIGN - Enhanced]
${qualityEnhanced}

[DIRECTION - Enhanced]
${directionEnhanced}

[DESIGN SYSTEM - Enhanced]
${systemEnhanced}

[SCOPE - Enhanced]
${scopeEnhanced}

[SUCCESS - Enhanced]
${successEnhanced}

---
ORIGINAL COACHING INSIGHTS PRESERVED:
${brandPositioning.text ? `\nBrand Positioning: ${brandPositioning.text.substring(0, 200)}...` : ""}
${visualAssets.text ? `\nVisual Assets: ${visualAssets.text.substring(0, 200)}...` : ""}
${customDesignSystem.text ? `\nCustom Design System: ${customDesignSystem.text.substring(0, 200)}...` : ""}
`;

      setEnhancedPrompt({ text: finalEnhanced, loading: false });
      setEnhancementProgress({ step: totalSteps, total: totalSteps, currentStep: "✓ Enhancement complete!", active: false });

    } catch (error) {
      setEnhancedPrompt({ text: "Error during multi-step enhancement. Please try again.", loading: false });
      setEnhancementProgress({ step: 0, total: 0, currentStep: "", active: false });
    }
  }

  const DESIGN_STEWARD_SYSTEM = `You are Design Steward AI, an expert product design review and prompt-improvement agent.

You combine expertise in: design thinking, UX and UI design, design literacy, branding, design systems, HCI, usability, accessibility, inclusive design, ethical behavior design, and sustainable product design.

Your job: Review screenshots of product UIs, prompts for AI agents, or concepts submitted by workshop participants. Provide honest, principled, actionable feedback.

TONE: Direct, constructive, specific. No fluff. Not overly positive or encouraging. State what is working and what is not. Identify risks. Make concrete recommendations.

STRUCTURE:
A. First assessment (30 words or less — what stands out most)
B. What is working (3–7 strengths)
C. Key risks and issues
D. Priority fixes (High / Medium / Low)
E. Improved prompt (rewritten for better AI output)
F. Scorecard (each category + Final score /100)
G. Confidence level (High / Medium / Low)
H. What to test next (3 suggested validations)

Be direct, principled, and constructive. Do not default to praise. State assumptions explicitly.`;

  const DESIGN_STEWARD_RESEARCH_MODE = `

RESEARCH & EVIDENCE MODE (active): Provide the same level of depth as a design research consultant.

When in this mode, your response MUST include:
1. **What research says** — Cite relevant findings from color psychology, HCI, usability studies, accessibility standards (WCAG), cognitive load research, or learning science. Name the domains (e.g. "Research on extended reading sessions suggests...", "Color psychology indicates...").
2. **Evidence-informed assessment** — Ground your critique in principles, not opinion. Distinguish between "research suggests" and "best practice" and "design principle."
3. **Concrete recommendations** — Specific, actionable changes with rationale. Not vague — e.g. "Lighten bgDeep from #0d1f2d to #152a38" not "consider lighter colors."
4. **Alternative directions** — 2–3 distinct options (e.g. "Option A: Hybrid... Option B: Lighten base... Option C: Warmer neutrals") with pros/cons.
5. **Confidence level** — High / Medium / Low and why (e.g. "High — well-established in readability research").

Use the same structure as the base system prompt (A–H), but ground every point in research or principle, and add the alternative directions section before "What to test next."`;

  async function callDesignSteward() {
    const input = designAssistantInput.trim();
    const hasImage = !!designAssistantImage;
    if (!input && !hasImage) {
      setDesignAssistantAi({ r: "Add a screenshot and/or paste a prompt, concept, or design question (colors, typography, layout, flows, accessibility — any design aspect). Use 'Review my current work' to load your brief and direction.", l: false });
      return;
    }
    setDesignAssistantAi({ r: "", l: true });
    const textPart = input ? `\n\n--- TEXT/PROMPT TO REVIEW ---\n${input}\n---` : "";
    const context = `Review the following as Design Steward AI. Provide a full review with assessment, risks, recommendations, improved prompt (if applicable), scorecard, and next steps.${hasImage ? "\n\nThe user has attached a screenshot of their coded UI/prototype from Claude or Cursor. Assess ALL relevant design aspects: color and contrast, typography, layout, spacing, hierarchy, navigation, flows, feedback states, accessibility, branding, interaction patterns, and any generic AI patterns. Be specific about what you observe across the full design." : ""}${textPart}`;
    const systemPrompt = designAssistantResearchMode ? DESIGN_STEWARD_SYSTEM + DESIGN_STEWARD_RESEARCH_MODE : DESIGN_STEWARD_SYSTEM;
    const imageForApi = hasImage ? designAssistantImage : null;
    try {
      const r = await callAI(systemPrompt, context, 3000, imageForApi);
      setDesignAssistantAi({ r, l: false });
    } catch (e) {
      setDesignAssistantAi({ r: "Something went wrong. Try again.", l: false });
    }
  }

  function fillDesignAssistantFromWork() {
    const parts = [
      "PROJECT CONTEXT",
      fields.context || "(Not yet defined)",
      "",
      "DESIGN DIRECTION",
      fields.direction || "(Not yet defined)",
      "",
      "DESIGN SYSTEM",
      fields.system || "(Not yet defined)",
      "",
      "SCOPE & SUCCESS",
      fields.scope && `Scope: ${fields.scope}`,
      fields.success && `Success: ${fields.success}`,
    ].filter(Boolean).join("\n");
    setDesignAssistantInput(parts);
  }

  async function getSystemPrinciples() {
    setSystemPrinciples({ text: "", loading: true });
    const context = fields.context || "Not yet defined";
    const direction = fields.direction || "Not yet defined";
    const system = fields.system || "Not yet defined";

    const t = await callAI(
      `You are a design system expert helping a workshop participant build a cohesive design language. Provide exactly 5 top principles for creating a strong design system. Each principle should be one clear sentence. Format as a numbered list (1-5). Be specific, actionable, and relevant to building with AI. Max 120 words total.`,
      `Participant: ${name}\n\nContext: ${context}\n\nDirection: ${direction}\n\nCurrent system definition: ${system}`
    );
    setSystemPrinciples({ text: t, loading: false });
  }

  async function answerSystemQuestion() {
    if (!systemQuestion.trim()) return;
    setSystemAnswer({ text: "", loading: true });

    const context = fields.context || "Not yet defined";
    const direction = fields.direction || "Not yet defined";
    const system = fields.system || "Not yet defined";

    const t = await callAI(
      `You are a design system expert. Answer the participant's question about color, typography, or accessibility with specific, actionable guidance. Reference their context and current system if relevant. Be direct and practical. Max 150 words.`,
      `Participant: ${name}\n\nContext: ${context}\n\nDirection: ${direction}\n\nCurrent system: ${system}\n\nQuestion: ${systemQuestion}`
    );
    setSystemAnswer({ text: t, loading: false });
  }

  async function exploreDesignTopic(principleText) {
    setSystemTopicView({ topic: "", content: "", loading: true });

    // First, extract the design topic from the principle
    const topic = await callAI(
      `Extract the core design topic from this principle in 2-4 words (e.g., "visual hierarchy", "color theory", "interaction design", "cognitive load", "accessibility"). Return only the topic name, no explanation.`,
      principleText
    );

    // Then get educational content about that topic
    const content = await callAI(
      `You are a design educator teaching fundamental design concepts. Provide a concise educational summary about "${topic.trim()}" with:

1. Core principles (3-4 key concepts)
2. Practical application tips
3. 2-3 recommended resources to learn more (books, articles, or tools with authors/sources)

Focus on fundamental design knowledge from behavior design, interaction design, visual design, cognitive science, or UX research. Be specific and actionable. Max 220 words.`,
      `Topic: ${topic.trim()}\n\nContext: This is for a workshop participant learning to design and build with AI.`
    );

    setSystemTopicView({ topic: topic.trim(), content, loading: false });
  }

  async function getSpecsGuidance() {
    setSpecsGuidance({ text: "", loading: true });
    const direction = fields.direction || "Not yet defined";
    const context = fields.context || "Not yet defined";

    const t = await callAI(
      `You are a design coach helping a participant choose visual system specifications. Based on their design direction and product context, recommend:

1. Font scale (large/comfortable/compact)
2. Spacing (airy/balanced/compact)
3. Corner style (sharp/subtle/rounded)
4. Component style (minimal/subtle/defined)

For each, explain WHY it fits their direction in one sentence. Emphasize that quality design is simple with good whitespace by default. Airy spacing creates calm, breathing room, and professionalism—it's the foundation of modern design. Be specific and directive. Max 140 words.`,
      `Participant: ${name}\n\nContext: ${context}\n\nDesign Direction: ${direction}`
    );
    setSpecsGuidance({ text: t, loading: false });
  }

  function updateSystemFieldWithSpecs() {
    const FONT_SCALE_MAP = {
      large: "Font scale: Large (24px body, 64px+ headings) — Accessible, high readability",
      comfortable: "Font scale: Comfortable (16-18px body, 36-48px headings) — Balanced, professional",
      compact: "Font scale: Compact (14px body, 28-32px headings) — Dense, information-rich"
    };
    const SPACING_MAP = {
      airy: "Spacing: Airy (generous whitespace, 24-48px gaps) — Calm, breathable, modern. Quality design prioritizes simplicity and negative space.",
      balanced: "Spacing: Balanced (moderate whitespace, 16-24px gaps) — Organized, clear",
      compact: "Spacing: Compact (minimal whitespace, 8-12px gaps) — Dense, efficient"
    };
    const CORNER_MAP = {
      sharp: "Corners: Sharp (0px radius) — Modern, crisp, technical",
      subtle: "Corners: Subtle (4-8px radius) — Friendly, professional",
      rounded: "Corners: Rounded (12-20px radius) — Playful, approachable"
    };
    const COMPONENT_MAP = {
      minimal: "Components: Minimal (no decoration, clean lines) — Simple, elegant. Quality design doesn't need ornamentation.",
      subtle: "Components: Subtle (light borders/shadows) — Defined, structured",
      defined: "Components: Defined (clear boundaries, depth) — Layered, spatial"
    };

    const specsText = [
      FONT_SCALE_MAP[fontScale],
      SPACING_MAP[spacing],
      CORNER_MAP[cornerStyle],
      COMPONENT_MAP[componentStyle]
    ].join("\n");

    setFields(f => {
      const existing = f.system;
      // Remove old specifications if they exist
      const cleaned = existing.replace(/\n\nVisual System Specifications:[\s\S]*$/, "").trim();
      return { ...f, system: `${cleaned}\n\nVisual System Specifications:\n${specsText}` };
    });
  }

  function assemblePrompt() {
    return LAYERS.map(l => `[${L[l.key].label.toUpperCase()}]\n${fields[l.key]}`).join("\n\n");
  }

  // eslint-disable-next-line no-unused-vars
  async function getPromptReview() {
    setAi({ text: "", loading: true });
    const t = await callAI(
      `You are Azul Ceballos's design coaching assistant at a vibe coding workshop. A participant has written a six-layer prompt. Identify one layer that is doing particularly strong work and say why in one sentence. Then identify one layer that could be sharpened and give a specific suggestion — not generic advice, a concrete rewrite or addition. Warm, specific, 100 words max. No bullets. No preamble.`,
      `Participant: ${name}\n\nPrompt:\n${assemblePrompt()}`
    );
    setAi({ text: t, loading: false });
  }

  async function getSelfAssessment() {
    setAssessingMe(true);
    const participantData = {
      name,
      intention: { what: fields.context }, // Using context as intention proxy
      prompt_layers: fields
    };
    const assessment = await assessParticipant(participantData);
    setSelfAssessment(assessment);
    setAssessingMe(false);
  }

  function copyPrompt() {
    navigator.clipboard.writeText(assemblePrompt()).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  // eslint-disable-next-line no-redeclare
  function openInClaude() {
    navigator.clipboard.writeText(assemblePrompt()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
      window.open("https://claude.ai/new", "_blank");
    });
  }

  // Copy/paste functions for AI assistant features
  function generateBrandPositioningPrompt() {
    return `I need help discovering distinctive visual territories that align with my brand positioning.

PROJECT CONTEXT:
${fields.context || "(Not yet defined)"}

CURRENT DESIGN DIRECTION:
${fields.direction || "(Not yet defined)"}

YOUR TASK:
1. **Map the visual landscape** — What is the current visual language in my brand space? What does everyone else look like? Where are the blind spots—visual territories that align with my positioning but are underexplored?

2. **Identify the positioning disconnect** — Based on my context, what should my visual language communicate? Where might there be a gap between what I want to say (positioning) and what typical designs in this space actually say (visual tone)?

3. **Define a visual north star** — Give me ONE specific visual direction that anchors my brand positioning. Not generic adjectives ("clean", "modern")—concrete visual strategy with rationale.

${brandPositioning.text ? `\nCURRENT INSIGHTS:\n${brandPositioning.text}` : ""}

Be specific. Avoid generic design terms. Ground recommendations in psychology and semiotics.`;
  }

  function generateVisualAssetsPrompt() {
    return `I need recommendations for custom visual assets to make my product distinctive, not generic.

PROJECT CONTEXT:
${fields.context || "(Not yet defined)"}

DESIGN DIRECTION:
${fields.direction || "(Not yet defined)"}

BRAND POSITIONING:
${brandPositioning.text || "(Not yet defined)"}

YOUR TASK:
1. **Identify the asset types** — Based on my product and direction, which 2-3 types of visual assets would have the highest impact? (Icons, illustrations, animations, photography, 3D visuals, video, motion graphics, sound/music, textures, patterns, etc.)

2. **Define the visual style** — For each asset type, what specific visual style would align with my direction and positioning? Be concrete—reference real examples, art movements, or techniques.

3. **Commissioning brief** — For the #1 priority asset, write a 2-3 sentence creative brief I could give to a designer or AI tool.

${visualAssets.text ? `\nCURRENT RECOMMENDATIONS:\n${visualAssets.text}` : ""}

Be specific about visual style. Avoid generic terms. Prioritize impact and differentiation.`;
  }

  function generateCustomSystemPrompt() {
    return `I need a comprehensive, custom design system and brand inspiration specifically tailored to my product.

PROJECT CONTEXT:
${fields.context || "(Not yet defined)"}

QUALITY DESIGN REFERENCES:
${fields.references || "(Not yet defined)"}

DESIGN DIRECTION:
${fields.direction || "(Not yet defined)"}

YOUR TASK:
Create a complete design system including:

1. **Color Palette** — 5-7 specific colors with exact hex values. Include rationale for each color choice based on my product context and psychological/emotional associations.

2. **Typography** — Specific font pairing recommendations (name the fonts). Explain why these fonts work for my product and audience.

3. **Spacing & Layout** — Specific spacing scale (e.g., 8px base unit: 8, 16, 24, 32, 48, 64) and layout approach (grid system, rhythm, density).

4. **Visual Language** — Specific recommendations for corners (border-radius values), shadows/depth, component style, iconography style.

5. **Brand Inspiration** — 3-4 reference brands, products, or visual examples that embody similar principles. Explain what to borrow from each.

6. **Mood & Tone** — The emotional quality this system should convey, grounded in my product purpose.

${customDesignSystem.text ? `\nCURRENT SYSTEM:\n${customDesignSystem.text}` : ""}

Be extremely specific. Include exact values, real font names, hex codes. Make it ready to implement.`;
  }

  function copyBrandPositioningPrompt() {
    navigator.clipboard.writeText(generateBrandPositioningPrompt()).then(() => {
      setBrandPositioningCopied(true);
      setTimeout(() => setBrandPositioningCopied(false), 2500);
    });
  }

  function openBrandPositioningInClaude() {
    navigator.clipboard.writeText(generateBrandPositioningPrompt()).then(() => {
      setBrandPositioningCopied(true);
      setTimeout(() => setBrandPositioningCopied(false), 3000);
      window.open("https://claude.ai/new", "_blank");
    });
  }

  function copyVisualAssetsPrompt() {
    navigator.clipboard.writeText(generateVisualAssetsPrompt()).then(() => {
      setVisualAssetsCopied(true);
      setTimeout(() => setVisualAssetsCopied(false), 2500);
    });
  }

  function openVisualAssetsInClaude() {
    navigator.clipboard.writeText(generateVisualAssetsPrompt()).then(() => {
      setVisualAssetsCopied(true);
      setTimeout(() => setVisualAssetsCopied(false), 3000);
      window.open("https://claude.ai/new", "_blank");
    });
  }

  function copyCustomSystemPrompt() {
    navigator.clipboard.writeText(generateCustomSystemPrompt()).then(() => {
      setCustomSystemCopied(true);
      setTimeout(() => setCustomSystemCopied(false), 2500);
    });
  }

  function openCustomSystemInClaude() {
    navigator.clipboard.writeText(generateCustomSystemPrompt()).then(() => {
      setCustomSystemCopied(true);
      setTimeout(() => setCustomSystemCopied(false), 3000);
      window.open("https://claude.ai/new", "_blank");
    });
  }

  // Coaching Feedback Prompt
  function generateCoachingPrompt() {
    return `I'm working on a six-layer prompt for a design project and need coaching feedback.

MY COMPLETE PROMPT:
${assemblePrompt()}

YOUR TASK:
As a design coaching assistant, please:

1. **Identify one strength** — Tell me one layer that is doing particularly strong work and explain why in one sentence.

2. **Give one specific improvement** — Tell me one layer that could be sharpened and give a concrete suggestion. Not generic advice — a specific rewrite or addition I can use.

Keep your response warm, specific, and around 100 words. No bullets. No preamble. Be direct and constructive.`;
  }

  // Enhancement Prompt for Mockup
  function generateEnhancementPrompt() {
    const allContext = `SIX-LAYER PROMPT:
${assemblePrompt()}

${brandPositioning.text ? `BRAND POSITIONING INSIGHTS:\n${brandPositioning.text}\n\n` : ""}${visualAssets.text ? `VISUAL ASSETS RECOMMENDATIONS:\n${visualAssets.text}\n\n` : ""}${customDesignSystem.text ? `CUSTOM DESIGN SYSTEM:\n${customDesignSystem.text}\n\n` : ""}${ai.text ? `COACHING FEEDBACK:\n${ai.text}\n\n` : ""}${selfAssessment ? `AI ASSESSMENT:\nStatus: ${selfAssessment.status}\nAnalysis: ${selfAssessment.analysis}\n\n` : ""}DESIGN SELECTIONS:
- Selected Color: ${selectedColor || "Not selected"}
- Selected Font: ${selectedFont ? (typeof selectedFont === 'object' ? selectedFont.name : selectedFont) : "Not selected"}
- Key Priorities: ${selectedPriorities.join(", ") || "Not selected"}
- Experience Tone: ${experienceTone || "Not specified"}
- Specifications: Font scale: ${fontScale}, Spacing: ${spacing}, Corners: ${cornerStyle}, Components: ${componentStyle}`;

    return `I need help enhancing my six-layer prompt to be implementation-ready for generating a high-quality HTML/CSS mockup.

${allContext}

YOUR TASK:
Transform each layer from abstract to concrete and implementation-ready. For each of the 6 layers below, provide an enhanced version that:

**1. CONTEXT (Enhanced)**
Transform abstract user descriptions into concrete user scenarios, use cases, and technical requirements. Include specific device context, time constraints, emotional states, and what success looks like for this user. Be specific and technical. (Max 150 words)

**2. QUALITY DESIGN (Enhanced)**
Extract specific, actionable design patterns from the references. Identify visual properties, interaction patterns, layout strategies, and design principles. Be concrete - name specific techniques, not abstract qualities. (Max 150 words)

**3. DIRECTION (Enhanced)**
Convert abstract aesthetic terms into concrete visual specifications. Replace "modern" with specific properties, "friendly" with UI choices, "professional" with layout decisions. Use color psychology, typography voice, spatial rhythm. (Max 200 words)

**4. DESIGN SYSTEM (Enhanced)**
Provide complete technical specifications ready for implementation. Include exact hex values, font stacks, spacing values (px), border radius, shadow definitions, animation specs. Make it copy-paste ready. (Max 250 words)

**5. SCOPE (Enhanced)**
Add technical implementation guidance: HTML semantic structure, component hierarchy, accessibility attributes (ARIA), responsive breakpoints, interaction states, state management. Make it implementation-ready. (Max 200 words)

**6. SUCCESS (Enhanced)**
Transform abstract success into measurable, observable criteria. How will we know the mockup succeeded? What specific user experience signals matter? Define measurable success criteria and design validation checkpoints. (Max 150 words)

Format your response with clear section headers for each enhanced layer. Be specific, technical, and implementation-focused.`;
  }

  function copyCoachingPrompt() {
    navigator.clipboard.writeText(generateCoachingPrompt()).then(() => {
      setCoachingPromptCopied(true);
      setTimeout(() => setCoachingPromptCopied(false), 2500);
    });
  }

  function openCoachingInClaude() {
    navigator.clipboard.writeText(generateCoachingPrompt()).then(() => {
      setCoachingPromptCopied(true);
      setTimeout(() => setCoachingPromptCopied(false), 3000);
      window.open("https://claude.ai/new", "_blank");
    });
  }

  function copyEnhancementPrompt() {
    navigator.clipboard.writeText(generateEnhancementPrompt()).then(() => {
      setEnhancementPromptCopied(true);
      setTimeout(() => setEnhancementPromptCopied(false), 2500);
    });
  }

  function openEnhancementInClaude() {
    navigator.clipboard.writeText(generateEnhancementPrompt()).then(() => {
      setEnhancementPromptCopied(true);
      setTimeout(() => setEnhancementPromptCopied(false), 3000);
      window.open("https://claude.ai/new", "_blank");
    });
  }

  // Update local fields when initialData changes (from demo load)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFields(initialData);
      setLayerIdx(0);
    }
  }, [initialData]);

  // Update system field when specifications change
  useEffect(() => {
    if (layerIdx === 3) { // Design system layer
      updateSystemFieldWithSpecs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontScale, spacing, cornerStyle, componentStyle]);

  function next() {
    saveParticipantData(name, "prompt_layers", fields);
    onNext(fields);
  }

  if (preview) {
    const hasAssessed = selfAssessment !== null;
    const needsWork = hasAssessed && (selfAssessment.status === "needs-attention" || selfAssessment.status === "stuck" || selfAssessment.status === "needs-work" || selfAssessment.status === "incomplete");

    return (
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.15em", color: T.terra, marginBottom: 16, textTransform: "uppercase" }}>Step 02 · Preview & Edit</div>
        <h2 style={{ fontFamily: serif, fontSize: type.h2, fontWeight: 400, color: T.cream, margin: "0 0 12px" }}>Your complete prompt</h2>
        <p style={{ fontFamily: sans, fontSize: type.ui, color: T.white, margin: "0 0 24px" }}>
          All layers editable. Make changes, copy the complete prompt, or get AI assessment below.
        </p>

        {/* Copy Complete Prompt - Top */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <Btn onClick={copyPrompt} variant="primary" style={{ flex: 1 }}>{copied ? "Copied complete prompt ✓" : "📋 Copy complete prompt"}</Btn>
          <Btn onClick={openInClaude} variant="cream">{copiedForClaude ? "Copied ✓" : "Open in Claude →"}</Btn>
        </div>

        {/* Editable Layers */}
        <div style={{ background: T.cream, borderRadius: 16, overflow: "hidden", marginBottom: 32 }}>
          {LAYERS.map((l, idx) => {
            const ls = L[l.key];
            return (
              <div key={l.key} style={{ background: ls.pale, padding: "24px 32px", borderBottom: idx < LAYERS.length - 1 ? `1px solid ${T.ruleCard}` : "none" }}>
                <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: ls.accent, textTransform: "uppercase", marginBottom: 12 }}>
                  {l.num} · {ls.label}
                </div>
                {l.combined ? (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: ls.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Scope</div>
                      <Textarea
                        value={fields.scope}
                        onChange={v => setFields(f => ({ ...f, scope: v }))}
                        placeholder="What are you building?"
                        rows={4}
                        style={{ background: "rgba(255,255,255,0.8)", border: `1.5px solid ${ls.accent}40` }}
                      />
                    </div>
                    <div>
                      <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: ls.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Success</div>
                      <Textarea
                        value={fields.success}
                        onChange={v => setFields(f => ({ ...f, success: v }))}
                        placeholder="What does done feel like?"
                        rows={3}
                        style={{ background: "rgba(255,255,255,0.8)", border: `1.5px solid ${ls.accent}40` }}
                      />
                    </div>
                  </>
                ) : (
                  <Textarea
                    value={fields[l.key]}
                    onChange={v => setFields(f => ({ ...f, [l.key]: v }))}
                    placeholder={`${ls.label} layer…`}
                    rows={l.rows || 4}
                    style={{ background: "rgba(255,255,255,0.8)", border: `1.5px solid ${ls.accent}40` }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Assessment Section */}
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontFamily: serif, fontSize: type.h3, fontWeight: 400, color: T.cream, margin: "0 0 16px" }}>AI Assessment & Feedback</h3>

          {!hasAssessed && (
            <div style={{ background: "rgba(196,154,42,0.08)", border: "1.5px solid rgba(196,154,42,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
              <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: T.terra, marginBottom: 4 }}>
                ✨ Get feedback to improve
              </div>
              <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6 }}>
                Get an objective AI assessment of your work, or use the copy/paste prompts below to get coaching and enhancement from your own Claude/ChatGPT.
              </div>
            </div>
          )}

          {/* AI Assessment */}
          <div style={{ marginBottom: 20 }}>
            <Btn onClick={getSelfAssessment} variant="primary" disabled={assessingMe} style={{ width: "100%" }}>
              {assessingMe ? "Analyzing…" : hasAssessed ? "↻ Re-assess" : "✨ Get AI assessment"}
            </Btn>
          </div>

          {/* Assessment Result Card */}
          {selfAssessment && (
            <div style={{ marginBottom: 24, background: T.cream, borderRadius: 12, padding: "28px 32px" }}>
              {/* Header with status */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", color: T.terra, textTransform: "uppercase" }}>
                  Your AI Assessment
                </div>
                <div style={{
                  fontFamily: sans,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: T.white,
                  background: (() => {
                    const colors = {
                      excelling: "#7eb89a",
                      "on-track": "#6b9ad6",
                      "needs-attention": "#d4a574",
                      stuck: "#b85a5a",
                      strong: "#7eb89a",
                      developing: "#6b9ad6",
                      "needs-work": "#d4a574",
                      incomplete: "#b85a5a"
                    };
                    return colors[selfAssessment.status] || T.ink4;
                  })(),
                  padding: "6px 12px",
                  borderRadius: 6
                }}>
                  {selfAssessment.status === "excelling" && "Excelling ✓"}
                  {selfAssessment.status === "on-track" && "On Track"}
                  {selfAssessment.status === "needs-attention" && "Needs Attention"}
                  {selfAssessment.status === "stuck" && "Stuck"}
                  {selfAssessment.status === "strong" && "Strong ✓"}
                  {selfAssessment.status === "developing" && "Developing"}
                  {selfAssessment.status === "needs-work" && "Needs Work"}
                  {selfAssessment.status === "incomplete" && "Incomplete"}
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>
                  Progress: {selfAssessment.completedLayers} / {selfAssessment.totalLayers} layers completed
                </div>
                <div style={{ background: "rgba(0,0,0,0.08)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                  <div style={{
                    background: (() => {
                      const colors = {
                        excelling: "#7eb89a",
                        "on-track": "#6b9ad6",
                        "needs-attention": "#d4a574",
                        stuck: "#b85a5a",
                        strong: "#7eb89a",
                        developing: "#6b9ad6",
                        "needs-work": "#d4a574",
                        incomplete: "#b85a5a"
                      };
                      return colors[selfAssessment.status] || T.ink4;
                    })(),
                    height: "100%",
                    width: `${(selfAssessment.completedLayers / selfAssessment.totalLayers) * 100}%`,
                    transition: "width 0.3s ease"
                  }} />
                </div>
              </div>

              {/* AI Feedback */}
              <div style={{ fontFamily: sans, fontSize: 15, color: T.ink2, lineHeight: 1.7, marginBottom: 20 }}>
                {selfAssessment.analysis.split('\n').map((line, idx) => {
                  // Handle headers
                  if (line.startsWith('**') && line.endsWith('**')) {
                    const text = line.slice(2, -2);
                    return (
                      <div key={idx} style={{ fontWeight: 700, fontSize: 16, color: T.terra, marginTop: idx > 0 ? 20 : 0, marginBottom: 8, letterSpacing: "0.02em" }}>
                        {text}
                      </div>
                    );
                  }

                  // Handle numbered lists
                  const listMatch = line.match(/^(\d+)\.\s+(.+)$/);
                  if (listMatch) {
                    return (
                      <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, paddingLeft: 8 }}>
                        <span style={{ fontWeight: 600, color: T.terra, minWidth: 20 }}>{listMatch[1]}.</span>
                        <span>{listMatch[2]}</span>
                      </div>
                    );
                  }

                  // Handle bold inline text within paragraphs
                  const parts = line.split(/(\*\*[^*]+\*\*)/g);
                  const rendered = parts.map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={i} style={{ fontWeight: 600, color: T.ink }}>{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  });

                  // Empty lines
                  if (!line.trim()) {
                    return <div key={idx} style={{ height: 8 }} />;
                  }

                  // Regular paragraphs
                  return <div key={idx} style={{ marginBottom: 6 }}>{rendered}</div>;
                })}
              </div>

              {/* Integrate Assessment Button */}
              <button
                onClick={() => {
                  // Parse the assessment to find specific layer recommendations
                  const layerKeys = ['context', 'references', 'direction', 'system', 'scope', 'success'];
                  const feedback = `\n\n[AI Assessment]\n${selfAssessment.analysis}`;

                  // Append to the weakest layer (or first incomplete)
                  const targetLayer = layerKeys.reduce((min, key) =>
                    (!fields[min] || fields[key].length < fields[min].length) ? key : min
                  );

                  setFields(f => ({
                    ...f,
                    [targetLayer]: f[targetLayer].trim() ? `${f[targetLayer]}${feedback}` : selfAssessment.analysis
                  }));
                  // Don't clear selfAssessment - keep it so hasAssessed stays true
                }}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: T.terra,
                  color: T.white,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  marginBottom: 20
                }}
              >
                ✓ Integrate assessment feedback to my work
              </button>

              {/* Rubric info */}
              <details style={{ marginTop: 20, padding: "16px 20px", background: "rgba(0,0,0,0.03)", borderRadius: 8 }}>
                <summary style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: T.ink3, cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  What we're looking for (Assessment Rubric)
                </summary>
                <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
                  {Object.entries(ASSESSMENT_CRITERIA).map(([key, val]) => (
                    <div key={key} style={{ paddingBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                      <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: T.ink2, marginBottom: 4, textTransform: "uppercase" }}>
                        {key} <span style={{ color: T.ink4 }}>({val.weight}%)</span>
                      </div>
                      <div style={{ fontFamily: sans, fontSize: 13, color: T.ink3, lineHeight: 1.5 }}>
                        {val.criteria}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Coaching Feedback - Copy/Paste */}
          <div style={{ background: "rgba(74,124,98,0.06)", border: "1.5px solid rgba(74,124,98,0.15)", borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: "#4a7c62", textTransform: "uppercase", marginBottom: 4 }}>
              💬 Coaching Feedback
            </div>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6, marginBottom: 12 }}>
              Get specific coaching on your prompt — one strength and one concrete improvement suggestion.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={copyCoachingPrompt}
                disabled={!allFilled}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: !allFilled ? "rgba(0,0,0,0.1)" : T.cream,
                  color: !allFilled ? "rgba(0,0,0,0.4)" : T.ink,
                  cursor: !allFilled ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                  flex: 1
                }}
              >
                {coachingPromptCopied ? "Copied ✓" : "Copy prompt"}
              </button>
              <button
                onClick={openCoachingInClaude}
                disabled={!allFilled}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: !allFilled ? "rgba(0,0,0,0.1)" : "#4a7c62",
                  color: !allFilled ? "rgba(0,0,0,0.4)" : T.white,
                  cursor: !allFilled ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                  flex: 1
                }}
              >
                {coachingPromptCopied ? "Copied! Now paste in Claude ✓" : "Open in Claude →"}
              </button>
            </div>
          </div>

          {/* Multi-step Enhancement - Copy/Paste */}
          <div style={{ background: "rgba(91,158,214,0.06)", border: "1.5px solid rgba(91,158,214,0.15)", borderRadius: 12, padding: "16px 20px" }}>
            <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: "#5b9ed6", textTransform: "uppercase", marginBottom: 4 }}>
              ⚡ Multi-Step Enhancement
            </div>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6, marginBottom: 12 }}>
              Transform your prompt into implementation-ready specifications with concrete details for each layer. Includes all AI insights you've generated.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={copyEnhancementPrompt}
                disabled={!allFilled}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: !allFilled ? "rgba(0,0,0,0.1)" : T.cream,
                  color: !allFilled ? "rgba(0,0,0,0.4)" : T.ink,
                  cursor: !allFilled ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                  flex: 1
                }}
              >
                {enhancementPromptCopied ? "Copied ✓" : "Copy prompt"}
              </button>
              <button
                onClick={openEnhancementInClaude}
                disabled={!allFilled}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: !allFilled ? "rgba(0,0,0,0.1)" : "#5b9ed6",
                  color: !allFilled ? "rgba(0,0,0,0.4)" : T.white,
                  cursor: !allFilled ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                  flex: 1
                }}
              >
                {enhancementPromptCopied ? "Copied! Now paste in Claude ✓" : "Open in Claude →"}
              </button>
            </div>
          </div>
        </div>

        {ai.text && (
          <div style={{ marginTop: 32, background: T.bgSurf, borderRadius: 12, padding: "28px 32px" }}>
            <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", color: T.terra, marginBottom: 12, textTransform: "uppercase" }}>Coach review</div>
            <p style={{ fontFamily: serif, fontSize: type.bodySm, color: T.cream, lineHeight: 1.8, margin: "0 0 20px" }}>{ai.text}</p>
            <button
              onClick={() => {
                // Find which layer needs the most improvement and append there
                // For now, append to all layers as a general note
                const layerKeys = ['context', 'references', 'direction', 'system', 'scope', 'success'];
                const feedback = `\n\n[Coach Feedback]\n${ai.text}`;

                // Append to the first incomplete or shortest layer
                const targetLayer = layerKeys.reduce((min, key) =>
                  (!fields[min] || fields[key].length < fields[min].length) ? key : min
                );

                setFields(f => ({
                  ...f,
                  [targetLayer]: f[targetLayer].trim() ? `${f[targetLayer]}${feedback}` : ai.text
                }));
                setAi({ text: "", loading: false });
              }}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                fontWeight: 600,
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: T.terra,
                color: T.white,
                cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              ✓ Integrate coach feedback to my work
            </button>
          </div>
        )}

        {/* Enhanced Prompt Progress Bar */}
        {enhancementProgress.active && (
          <div style={{ marginTop: 32, background: "rgba(91,158,214,0.08)", border: "1.5px solid rgba(91,158,214,0.2)", borderRadius: 12, padding: "28px 32px" }}>
            <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", color: "#5b9ed6", marginBottom: 12, textTransform: "uppercase" }}>
              ⚡ Multi-Step Enhancement in Progress
            </div>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6, marginBottom: 16 }}>
              Enhancing your prompt layer by layer with all AI insights included. This may take 2-3 minutes.
            </div>

            {/* Progress Bar */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: "#5b9ed6" }}>
                  Step {enhancementProgress.step} of {enhancementProgress.total}
                </div>
                <div style={{ fontFamily: sans, fontSize: 13, color: T.white, opacity: 0.7 }}>
                  {Math.round((enhancementProgress.step / enhancementProgress.total) * 100)}%
                </div>
              </div>
              <div style={{ background: "rgba(0,0,0,0.2)", borderRadius: 8, height: 12, overflow: "hidden" }}>
                <div style={{
                  background: "linear-gradient(90deg, #5b9ed6 0%, #7eb89a 100%)",
                  height: "100%",
                  width: `${(enhancementProgress.step / enhancementProgress.total) * 100}%`,
                  transition: "width 0.5s ease",
                  borderRadius: 8
                }} />
              </div>
            </div>

            {/* Current Step Description */}
            <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6, fontStyle: "italic", opacity: 0.9 }}>
              {enhancementProgress.currentStep}
            </div>
          </div>
        )}

        {/* Enhanced Prompt for Claude Mockup */}
        {enhancedPrompt.text && !enhancedPrompt.loading && (
          <div style={{ marginTop: 32, background: "rgba(91,158,214,0.08)", border: "1.5px solid rgba(91,158,214,0.2)", borderRadius: 12, padding: "28px 32px" }}>
            <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", color: "#5b9ed6", marginBottom: 12, textTransform: "uppercase" }}>
              ✨ Enhanced Prompt for Claude Mockup
            </div>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6, marginBottom: 20 }}>
              Multi-step enhancement complete! This version includes all AI insights (brand positioning, visual assets, custom design system, coaching feedback) and specific technical details. Copy it and paste into Claude to generate a high-quality HTML/CSS mockup.
            </div>
            <div style={{ background: T.cream, borderRadius: 12, padding: "24px 28px", marginBottom: 20, maxHeight: 400, overflowY: "auto" }}>
              <pre style={{ fontFamily: sans, fontSize: 14, color: T.ink, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                {enhancedPrompt.text}
              </pre>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(enhancedPrompt.text).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2500);
                  });
                }}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1.5px solid #5b9ed6",
                  background: "#5b9ed6",
                  color: T.white,
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
              >
                {copied ? "Copied ✓" : "Copy enhanced prompt"}
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(enhancedPrompt.text).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 3000);
                    window.open("https://claude.ai/new", "_blank");
                  });
                }}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1.5px solid #5b9ed6",
                  background: "transparent",
                  color: "#5b9ed6",
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
              >
                Open in Claude →
              </button>
              <button
                onClick={() => {
                  setEnhancedPrompt({ text: "", loading: false });
                }}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  color: T.white,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "10px 20px",
                  opacity: 0.6
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Steering Formula */}
        <div style={{ marginTop: 40, marginBottom: 32 }}>
          <h3 style={{ fontFamily: serif, fontSize: type.h3, fontWeight: 400, color: T.cream, margin: "0 0 16px" }}>Steering Formula</h3>
          <div style={{ background: T.forest, borderRadius: 12, padding: "24px 28px" }}>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.gold, marginBottom: 16, lineHeight: 1.6 }}>
              After Claude builds from your prompt, use this formula to iterate. Don't rebuild from scratch — steer.
            </div>
            {[
              ["What to keep", "Name specifically what's working. Claude needs the anchor or it rebuilds from scratch."],
              ["What to change", "Name the one thing that's off. Not three things — one."],
              ["What's next", "One scope addition only. Scope creep in the prompt creates scope creep in the code."]
            ].map(([title, description]) => (
              <div key={title} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid rgba(245,240,230,0.1)" }}>
                <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: T.cream, marginBottom: 4 }}>{title}</div>
                <div style={{ fontFamily: sans, fontSize: 13, color: T.white, lineHeight: 1.6, opacity: 0.9 }}>{description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Continue section */}
        <div style={{ marginTop: 40 }}>
          {needsWork && (
            <div style={{ background: "rgba(184,90,90,0.08)", border: "1.5px solid rgba(184,90,90,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
              <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: "#b85a5a", marginBottom: 4 }}>
                Consider strengthening your prompt
              </div>
              <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6 }}>
                Your assessment shows areas for improvement. Edit layers directly above, get more feedback, then re-assess — or continue with what you have.
              </div>
            </div>
          )}

          {hasAssessed && (selfAssessment.status === "excelling" || selfAssessment.status === "strong") && (
            <div style={{ background: "rgba(126,184,154,0.08)", border: "1.5px solid rgba(126,184,154,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
              <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: "#7eb89a", marginBottom: 4 }}>
                Excellent work! ✓
              </div>
              <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6 }}>
                Your prompt shows strong design thinking. Copy it, open Claude, and build. Use the steering formula above to iterate.
              </div>
            </div>
          )}

          {hasAssessed && (selfAssessment.status === "on-track" || selfAssessment.status === "developing") && (
            <div style={{ background: "rgba(107,154,214,0.08)", border: "1.5px solid rgba(107,154,214,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
              <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: "#6b9ad6", marginBottom: 4 }}>
                Good progress
              </div>
              <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6 }}>
                Your prompt is solid. Copy it and start building in Claude, or refine it further with the tools above.
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
            <button
              onClick={() => { setPreview(false); setLayerIdx(0); }}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                color: T.white,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
                opacity: 0.6
              }}
            >
              ← Back to layers
            </button>
            <Btn
              onClick={next}
              disabled={!hasAssessed}
              variant={hasAssessed ? "primary" : "ghost"}
            >
              {!hasAssessed ? "Get assessment first →" : "I'm done building →"}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.15em", color: T.terra, marginBottom: 16, textTransform: "uppercase" }}>Step 02 · Prompt anatomy</div>
      <h2 style={{ fontFamily: serif, fontSize: type.h2, fontWeight: 400, color: T.cream, margin: "0 0 12px" }}>Six layers. Every prompt.</h2>
      <p style={{ fontFamily: sans, fontSize: type.ui, color: T.white, margin: "0 0 24px" }}>Build your prompt one layer at a time.</p>

      {/* Demo Loader - Always visible */}
      <div style={{ background: "rgba(196,154,42,0.08)", border: "1.5px solid rgba(196,154,42,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: T.terra, marginBottom: 4 }}>
            {demoLoaded ? "Demo example loaded" : "See a complete example"}
          </div>
          <div style={{ fontFamily: sans, fontSize: 14, color: T.white, opacity: 0.8 }}>
            {demoLoaded
              ? "GreenSeason plant delivery — Click reload to reset all fields"
              : allFilled
                ? "Replace your current content with GreenSeason demo (all 6 layers)"
                : "Load GreenSeason plant delivery demo (all 6 layers filled)"}
          </div>
        </div>
        <button
          onClick={loadDemoData}
          style={{
            fontFamily: sans,
            fontSize: 14,
            fontWeight: 600,
            padding: "8px 16px",
            borderRadius: 8,
            border: `1.5px solid ${T.terra}`,
            background: demoLoaded ? T.terra : "transparent",
            color: demoLoaded ? T.white : T.terra,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 0.15s"
          }}
        >
          {demoLoaded ? "Reload demo" : allFilled ? "Overwrite with demo" : "Load demo"}
        </button>
      </div>

      {/* Horizontal layer track */}
      <div style={{ position: "relative", display: "flex", marginBottom: 32 }}>
        {/* Connecting line */}
        <div style={{
          position: "absolute",
          top: 20,
          left: "calc(100%/12)",
          right: "calc(100%/12)",
          height: 0,
          borderTop: "2px solid rgba(255,255,255,0.18)",
          zIndex: 0
        }} />

        {/* Layer steps */}
        {LAYERS.map((l, i) => {
          const ls = L[l.key];
          const isDone = i < layerIdx;
          const isCurrent = i === layerIdx;

          // Node styling based on state
          let nodeStyle = {};
          if (isCurrent) {
            nodeStyle = { background: ls.accent, color: T.ink };
          } else if (isDone) {
            nodeStyle = { background: ls.accent, color: T.ink };
          } else {
            // Upcoming - gray state (100% opaque)
            nodeStyle = { background: "#2a2d28", color: "rgba(255,255,255,0.6)" };
          }

          // Label styling based on state
          let labelColor = "rgba(255,255,255,0.3)";
          let labelWeight = 400;
          if (isDone) {
            labelColor = "rgba(255,255,255,0.35)";
          } else if (isCurrent) {
            labelColor = "rgba(255,255,255,0.9)";
            labelWeight = 500;
          }

          return (
            <div
              key={l.key}
              onClick={() => { setLayerIdx(i); setShowExample(false); }}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                zIndex: 1,
                transition: "all 0.2s ease"
              }}
            >
              <div style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: sans,
                fontSize: 14,
                fontWeight: 500,
                flexShrink: 0,
                transition: "all 0.2s ease",
                ...nodeStyle
              }}>
                {isDone ? "✓" : String(i + 1).padStart(2, "0")}
              </div>
              <span style={{
                fontFamily: sans,
                fontSize: 13,
                textAlign: "center",
                lineHeight: 1.3,
                maxWidth: 80,
                color: labelColor,
                fontWeight: labelWeight,
                transition: "all 0.2s ease"
              }}>
                {ls.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Active layer card */}
      <div style={{ background: lStyle.bg, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "32px 36px 28px" }}>
          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", color: lStyle.accent, marginBottom: 8 }}>
            LAYER {String(layerIdx + 1).padStart(2, "0")}
          </div>
          <div style={{ fontFamily: serif, fontSize: 22, fontWeight: 400, color: T.ink, marginBottom: 4 }}>
            {lStyle.label}
          </div>
          <div style={{ fontFamily: sans, fontSize: 13, color: "rgba(26,28,24,0.55)", marginBottom: 16 }}>
            {layer.tagline}
          </div>
          <p style={{ fontFamily: sans, fontSize: type.ui, color: T.ink2, lineHeight: 1.65, margin: "0 0 20px" }}>{layer.guide}</p>
          <button onClick={() => setShowExample(!showExample)}
            style={{ background: "none", border: "none", fontFamily: sans, fontSize: type.ui, color: lStyle.accent, cursor: "pointer", padding: 0, marginBottom: showExample ? 16 : 16 }}>
            {showExample ? "Hide example ↑" : "See an example →"}
          </button>
          {showExample && (
            <div style={{ background: "rgba(0,0,0,0.06)", borderRadius: 8, padding: "12px 16px", marginBottom: 14 }}>
              <p style={{ fontFamily: serif, fontSize: type.ui, color: T.ink2, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>{layer.example}</p>
            </div>
          )}
          {layer.key === "references" && (
            <>
              <div style={{ background: "rgba(106,78,113,0.08)", borderRadius: 8, padding: "16px 18px", marginBottom: 16 }}>
                <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: lStyle.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Design is not about imitation</div>
                <p style={{ fontFamily: sans, fontSize: type.uiSm, color: T.ink2, lineHeight: 1.6, margin: 0 }}>We're not trying to make your product look like Apple, Stripe, or anyone else. What we're doing is learning what makes those designs effective, when those approaches work, and how to apply those principles to your own context.</p>
              </div>
              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={loadExampleReferences}
                  style={{
                    fontFamily: sans,
                    fontSize: type.ui,
                    fontWeight: 600,
                    padding: "10px 18px",
                    borderRadius: 8,
                    border: `1.5px solid ${lStyle.accent}`,
                    background: lStyle.bg,
                    color: lStyle.accent,
                    cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                >
                  ✨ Load example — Apple, Nike, Airbnb
                </button>
              </div>
              {referenceUrls.map((ref, i) => (
                <div key={i} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: i < 2 ? `1px solid rgba(0,0,0,0.08)` : "none" }}>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink3, display: "block", marginBottom: 6 }}>Reference {i + 1} — URL</label>
                    <input
                      value={ref.url}
                      onChange={e => updateReferenceUrl(i, "url", e.target.value)}
                      placeholder="https://example.com"
                      style={{
                        width: "100%",
                        fontFamily: sans,
                        fontSize: type.ui,
                        color: T.ink,
                        background: "rgba(255,255,255,0.65)",
                        border: "1.5px solid rgba(0,0,0,0.12)",
                        borderRadius: 8,
                        padding: "10px 14px",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 400, color: T.ink3, display: "block", marginBottom: 6 }}>What stands out? (optional)</label>
                    <input
                      value={ref.notes}
                      onChange={e => updateReferenceUrl(i, "notes", e.target.value)}
                      placeholder="e.g. clarity, hierarchy, tone"
                      style={{
                        width: "100%",
                        fontFamily: sans,
                        fontSize: type.ui,
                        color: T.ink,
                        background: "rgba(255,255,255,0.65)",
                        border: "1.5px solid rgba(0,0,0,0.12)",
                        borderRadius: 8,
                        padding: "10px 14px",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontFamily: sans, fontSize: type.ui, color: T.ink }}>
                    <input
                      type="checkbox"
                      checked={ref.relevant}
                      onChange={e => updateReferenceUrl(i, "relevant", e.target.checked)}
                      style={{ marginRight: 8, cursor: "pointer" }}
                    />
                    Relevant to my product
                  </label>
                </div>
              ))}
              <div style={{ marginTop: 24, marginBottom: 20 }}>
                <button
                  onClick={getAiInterpretation}
                  disabled={!referenceUrls.some(r => r.url.trim()) || referenceAi.loading}
                  style={{
                    fontFamily: sans,
                    fontSize: type.ui,
                    fontWeight: 600,
                    padding: "12px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: lStyle.accent,
                    color: T.white,
                    cursor: referenceUrls.some(r => r.url.trim()) && !referenceAi.loading ? "pointer" : "not-allowed",
                    opacity: !referenceUrls.some(r => r.url.trim()) || referenceAi.loading ? 0.5 : 1,
                    transition: "all 0.15s"
                  }}
                >
                  {referenceAi.loading ? "Analyzing…" : "✨ Get AI interpretation"}
                </button>
              </div>
              {referenceAi.text && (
                <div style={{ background: "rgba(106,78,113,0.12)", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
                  <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.1em", color: lStyle.accent, marginBottom: 12, textTransform: "uppercase" }}>AI Interpretation</div>
                  <p style={{ fontFamily: sans, fontSize: type.ui, color: T.ink, lineHeight: 1.7, margin: "0 0 20px", whiteSpace: "pre-wrap" }}>{referenceAi.text}</p>
                  <button
                    onClick={() => {
                      setFields(f => ({
                        ...f,
                        references: f.references.trim() ? `${f.references}\n\n[AI Interpretation]\n${referenceAi.text}` : referenceAi.text
                      }));
                      setReferenceAi({ text: "", loading: false });
                    }}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "none",
                      background: lStyle.accent,
                      color: T.white,
                      cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                  >
                    ✓ Integrate AI interpretation to my work
                  </button>
                </div>
              )}
              <div style={{ background: lStyle.bg, borderRadius: 12, padding: "20px 24px", marginBottom: 0 }}>
                <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.1em", color: lStyle.accent, marginBottom: 10, textTransform: "uppercase" }}>Design Assistant</div>
                <p style={{ fontFamily: sans, fontSize: type.uiSm, color: T.ink2, lineHeight: 1.6, margin: "0 0 14px" }}>Design Steward AI — paste a screenshot for design review. Use Research mode for evidence-backed recommendations like our color analysis.</p>
                <button
                  onClick={() => { setDesignAssistantOpen(true); setDesignAssistantInput(""); setDesignAssistantImage(null); setDesignAssistantAi({ r: "", l: false }); }}
                  style={{
                    fontFamily: sans,
                    fontSize: type.ui,
                    fontWeight: 600,
                    padding: "10px 18px",
                    borderRadius: 8,
                    border: `1.5px solid ${lStyle.accent}`,
                    background: "transparent",
                    color: lStyle.accent,
                    cursor: "pointer",
                    transition: "all 0.15s"
                  }}
                >
                  ✨ Open Design Assistant
                </button>
              </div>
            </>
          )}
          {layer.key === "system" && (
            <>
              <ColorPicker selected={selectedColor} onSelect={handleColorSelect} />
              <FontPicker selected={selectedFont} onSelect={handleFontSelect} />

              <div style={{ background: "rgba(83,74,183,0.08)", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
                <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.1em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 10 }}>Design System Principles</div>
                <p style={{ fontFamily: sans, fontSize: type.uiSm, color: T.ink2, lineHeight: 1.6, margin: "0 0 14px" }}>Get 5 top principles for building a cohesive design system, then ask questions about color, typography, or accessibility.</p>

                {!systemPrinciples.text && !systemPrinciples.loading && (
                  <button
                    onClick={getSystemPrinciples}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "10px 18px",
                      borderRadius: 8,
                      border: `1.5px solid ${lStyle.accent}`,
                      background: "transparent",
                      color: lStyle.accent,
                      cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                  >
                    ✨ Get 5 principles
                  </button>
                )}

                {systemPrinciples.loading && (
                  <div style={{ fontFamily: sans, fontSize: type.ui, color: T.ink3, fontStyle: "italic" }}>
                    Loading principles...
                  </div>
                )}

                {systemPrinciples.text && !systemTopicView && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      {systemPrinciples.text.split(/(?=\d+\.)/).filter(p => p.trim()).map((principle, idx) => {
                        const cleanPrinciple = principle.trim();
                        if (!cleanPrinciple) return null;

                        return (
                          <div key={idx} style={{ background: T.white, borderRadius: 8, padding: "16px 20px", marginBottom: 12 }}>
                            <div style={{ fontFamily: sans, fontSize: type.ui, color: T.ink, lineHeight: 1.7, marginBottom: 12 }}>
                              {cleanPrinciple}
                            </div>
                            <button
                              onClick={() => exploreDesignTopic(cleanPrinciple)}
                              style={{
                                fontFamily: sans,
                                fontSize: 13,
                                fontWeight: 600,
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "none",
                                background: "rgba(83,74,183,0.1)",
                                color: lStyle.accent,
                                cursor: "pointer",
                                transition: "all 0.15s"
                              }}
                            >
                              Learn more →
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ borderTop: `1px solid rgba(0,0,0,0.1)`, paddingTop: 16, marginTop: 4, marginBottom: 12 }}>
                      <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: lStyle.accent, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>
                        Ask about color, typography, or accessibility
                      </label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input
                          type="text"
                          value={systemQuestion}
                          onChange={e => setSystemQuestion(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && answerSystemQuestion()}
                          placeholder="e.g. How do I choose accessible color contrast?"
                          style={{
                            flex: 1,
                            fontFamily: sans,
                            fontSize: type.ui,
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: "1.5px solid rgba(0,0,0,0.15)",
                            background: T.white,
                            color: T.ink,
                            outline: "none"
                          }}
                        />
                        <button
                          onClick={answerSystemQuestion}
                          disabled={!systemQuestion.trim() || systemAnswer.loading}
                          style={{
                            fontFamily: sans,
                            fontSize: type.ui,
                            fontWeight: 600,
                            padding: "10px 18px",
                            borderRadius: 8,
                            border: "none",
                            background: systemQuestion.trim() && !systemAnswer.loading ? lStyle.accent : "rgba(0,0,0,0.1)",
                            color: systemQuestion.trim() && !systemAnswer.loading ? T.white : "rgba(0,0,0,0.3)",
                            cursor: systemQuestion.trim() && !systemAnswer.loading ? "pointer" : "not-allowed",
                            transition: "all 0.15s"
                          }}
                        >
                          {systemAnswer.loading ? "..." : "Ask"}
                        </button>
                      </div>
                    </div>

                    {systemAnswer.text && (
                      <div style={{ background: T.white, borderRadius: 8, padding: "16px 20px" }}>
                        <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.1em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 8 }}>
                          Answer
                        </div>
                        <div style={{ fontFamily: sans, fontSize: type.ui, color: T.ink, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                          {systemAnswer.text}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Design Topic Detail View */}
                {systemTopicView && (
                  <div style={{ marginBottom: 16 }}>
                    <button
                      onClick={() => setSystemTopicView(null)}
                      style={{
                        fontFamily: sans,
                        fontSize: type.ui,
                        fontWeight: 600,
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: `1.5px solid ${lStyle.accent}`,
                        background: "transparent",
                        color: lStyle.accent,
                        cursor: "pointer",
                        marginBottom: 16,
                        transition: "all 0.15s"
                      }}
                    >
                      ← Back to 5 principles
                    </button>

                    {systemTopicView.loading && (
                      <div style={{ fontFamily: sans, fontSize: type.ui, color: T.ink3, fontStyle: "italic" }}>
                        Loading {systemTopicView.topic || "topic"}...
                      </div>
                    )}

                    {systemTopicView.content && (
                      <div style={{ background: T.white, borderRadius: 8, padding: "20px 24px" }}>
                        <div style={{ fontFamily: serif, fontSize: 24, fontWeight: 500, color: lStyle.accent, marginBottom: 16, textTransform: "capitalize" }}>
                          {systemTopicView.topic}
                        </div>
                        <div style={{ fontFamily: sans, fontSize: type.ui, color: T.ink, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                          {systemTopicView.content}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Visual System Specifications */}
              {!systemTopicView && (
                <div style={{ background: T.white, borderRadius: 12, padding: "24px 28px", marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                      <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.1em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 8 }}>Visual System Specifications</div>
                      <p style={{ fontFamily: sans, fontSize: 15, color: T.ink2, lineHeight: 1.6, margin: 0 }}>Select font scale, spacing, corners, and component style. These choices will be included in your prompt to Claude.</p>
                    </div>
                    <button
                      onClick={getSpecsGuidance}
                      disabled={specsGuidance.loading}
                      style={{
                        fontFamily: sans,
                        fontSize: 13,
                        fontWeight: 600,
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: `1.5px solid ${lStyle.accent}`,
                        background: "transparent",
                        color: lStyle.accent,
                        cursor: specsGuidance.loading ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        transition: "all 0.15s",
                        opacity: specsGuidance.loading ? 0.6 : 1
                      }}
                    >
                      {specsGuidance.loading ? "..." : "✨ Get guidance"}
                    </button>
                  </div>

                  {specsGuidance.text && (
                    <div style={{ background: "rgba(83,74,183,0.08)", borderRadius: 8, padding: "14px 18px", marginBottom: 20 }}>
                      <div style={{ fontFamily: sans, fontSize: 14, color: T.ink, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                        {specsGuidance.text}
                      </div>
                    </div>
                  )}

                  {/* Font Scale */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Font Scale</div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {[
                        { id: "large", label: "Large", desc: "24px body, accessible" },
                        { id: "comfortable", label: "Comfortable", desc: "16-18px body, professional" },
                        { id: "compact", label: "Compact", desc: "14px body, dense" }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setFontScale(opt.id)}
                          style={{
                            flex: "1 1 150px",
                            fontFamily: sans,
                            fontSize: 14,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: `1.5px solid ${fontScale === opt.id ? lStyle.accent : "rgba(0,0,0,0.15)"}`,
                            background: fontScale === opt.id ? `${lStyle.accent}15` : "transparent",
                            color: fontScale === opt.id ? lStyle.accent : T.ink2,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            textAlign: "left"
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{opt.label}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Spacing */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Spacing <span style={{ fontSize: 11, fontWeight: 400, color: T.ink3 }}>• Airy spacing creates calm & professionalism (recommended default)</span></div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {[
                        { id: "airy", label: "Airy", desc: "Generous whitespace, calm" },
                        { id: "balanced", label: "Balanced", desc: "Moderate whitespace" },
                        { id: "compact", label: "Compact", desc: "Minimal whitespace" }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setSpacing(opt.id)}
                          style={{
                            flex: "1 1 150px",
                            fontFamily: sans,
                            fontSize: 14,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: `1.5px solid ${spacing === opt.id ? lStyle.accent : "rgba(0,0,0,0.15)"}`,
                            background: spacing === opt.id ? `${lStyle.accent}15` : "transparent",
                            color: spacing === opt.id ? lStyle.accent : T.ink2,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            textAlign: "left"
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{opt.label}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Corner Style */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Corner Style</div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {[
                        { id: "sharp", label: "Sharp", desc: "0px, modern & technical" },
                        { id: "subtle", label: "Subtle", desc: "4-8px, professional" },
                        { id: "rounded", label: "Rounded", desc: "12-20px, playful" }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setCornerStyle(opt.id)}
                          style={{
                            flex: "1 1 150px",
                            fontFamily: sans,
                            fontSize: 14,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: `1.5px solid ${cornerStyle === opt.id ? lStyle.accent : "rgba(0,0,0,0.15)"}`,
                            background: cornerStyle === opt.id ? `${lStyle.accent}15` : "transparent",
                            color: cornerStyle === opt.id ? lStyle.accent : T.ink2,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            textAlign: "left"
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{opt.label}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Component Style */}
                  <div>
                    <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink, marginBottom: 10 }}>Component Style <span style={{ fontSize: 11, fontWeight: 400, color: T.ink3 }}>• Minimal is clean & elegant (recommended default)</span></div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {[
                        { id: "minimal", label: "Minimal", desc: "No decoration, clean" },
                        { id: "subtle", label: "Subtle", desc: "Light borders/shadows" },
                        { id: "defined", label: "Defined", desc: "Clear boundaries & depth" }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setComponentStyle(opt.id)}
                          style={{
                            flex: "1 1 150px",
                            fontFamily: sans,
                            fontSize: 14,
                            padding: "12px 16px",
                            borderRadius: 8,
                            border: `1.5px solid ${componentStyle === opt.id ? lStyle.accent : "rgba(0,0,0,0.15)"}`,
                            background: componentStyle === opt.id ? `${lStyle.accent}15` : "transparent",
                            color: componentStyle === opt.id ? lStyle.accent : T.ink2,
                            cursor: "pointer",
                            transition: "all 0.15s",
                            textAlign: "left"
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{opt.label}</div>
                          <div style={{ fontSize: 12, opacity: 0.8 }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Design System & Brand Inspiration */}
              {!systemTopicView && (
                <div style={{ marginTop: 24, padding: "20px 24px", background: "rgba(160,123,200,0.06)", border: "1.5px solid rgba(160,123,200,0.15)", borderRadius: 12 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 4 }}>
                      ✨ Custom Design System & Brand Inspiration
                    </div>
                    <div style={{ fontFamily: sans, fontSize: 14, color: T.ink3, lineHeight: 1.6, marginBottom: 12 }}>
                      Copy this guided prompt and paste it into Claude or ChatGPT to get a complete, custom design system tailored to your product — with specific colors (hex values), typography (font names), spacing scale, visual language, and brand inspiration.
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={copyCustomSystemPrompt}
                      disabled={(!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)}
                      style={{
                        fontFamily: sans,
                        fontSize: type.ui,
                        fontWeight: 600,
                        padding: "10px 20px",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: ((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.1)" : T.cream,
                        color: ((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.4)" : T.ink,
                        cursor: ((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) ? "not-allowed" : "pointer",
                        transition: "all 0.15s",
                        flex: 1
                      }}
                    >
                      {customSystemCopied ? "Copied ✓" : "Copy prompt"}
                    </button>
                    <button
                      onClick={openCustomSystemInClaude}
                      disabled={(!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)}
                      style={{
                        fontFamily: sans,
                        fontSize: type.ui,
                        fontWeight: 600,
                        padding: "10px 20px",
                        borderRadius: 8,
                        border: "none",
                        background: ((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.1)" : lStyle.accent,
                        color: ((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.4)" : T.white,
                        cursor: ((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) ? "not-allowed" : "pointer",
                        transition: "all 0.15s",
                        flex: 1
                      }}
                    >
                      {customSystemCopied ? "Copied! Now paste in Claude ✓" : "Open in Claude →"}
                    </button>
                  </div>

                  {((!fields.context || fields.context.trim().length < 50) || (!fields.references || fields.references.trim().length < 30) || (!fields.direction || fields.direction.trim().length < 30)) && (
                    <div style={{ marginTop: 12, fontFamily: sans, fontSize: 13, color: T.ink4, fontStyle: "italic" }}>
                      Complete Context, Quality Design, and Direction layers first to generate the prompt.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {layer.combined ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                  Scope
                </label>
                <Textarea
                  value={fields.scope}
                  onChange={v => setFields(f => ({ ...f, scope: v }))}
                  placeholder="What are you building? One component or screen — name the boundary."
                  rows={layer.scopeRows}
                  style={{ background: "rgba(255,255,255,0.65)", border: `1.5px solid rgba(0,0,0,0.12)` }}
                />
              </div>
              <div>
                <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                  Success
                </label>
                <Textarea
                  value={fields.success}
                  onChange={v => setFields(f => ({ ...f, success: v }))}
                  placeholder="What does done feel like? One sentence, in human terms."
                  rows={layer.successRows}
                  style={{ background: "rgba(255,255,255,0.65)", border: `1.5px solid rgba(0,0,0,0.12)` }}
                />
              </div>
            </>
          ) : (
            <Textarea value={fields[layer.key]} onChange={v => setFields(f => ({ ...f, [layer.key]: v }))}
              placeholder={`Write your ${lStyle.label.toLowerCase()} layer…`} rows={layer.rows}
              style={{ background: "rgba(255,255,255,0.65)", border: `1.5px solid rgba(0,0,0,0.12)` }}
            />
          )}
          {layer.key === "direction" && (
            <>
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 12 }}>
                  Key design priorities (choose up to 3)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: showCustomPriority ? 12 : 0 }}>
                  {["Clarity", "Speed", "Trust", "Simplicity", "Engagement", "Delight", "Control", "Exploration"].map(priority => {
                    const isSelected = selectedPriorities.includes(priority);
                    const isDisabled = !isSelected && selectedPriorities.length >= 3;
                    return (
                      <button
                        key={priority}
                        onClick={() => !isDisabled && handlePriorityToggle(priority)}
                        style={{
                          fontFamily: sans,
                          fontSize: type.ui,
                          fontWeight: isSelected ? 600 : 400,
                          padding: "8px 16px",
                          borderRadius: 20,
                          border: `1.5px solid ${isSelected ? lStyle.accent : "rgba(0,0,0,0.15)"}`,
                          background: isSelected ? lStyle.accent : "transparent",
                          color: isSelected ? T.white : isDisabled ? "rgba(0,0,0,0.3)" : T.ink,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          transition: "all 0.15s",
                          opacity: isDisabled ? 0.5 : 1
                        }}
                      >
                        {priority}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => selectedPriorities.length < 3 && setShowCustomPriority(!showCustomPriority)}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 400,
                      padding: "8px 16px",
                      borderRadius: 20,
                      border: `1.5px dashed ${showCustomPriority ? lStyle.accent : "rgba(0,0,0,0.15)"}`,
                      background: showCustomPriority ? "rgba(74,124,98,0.08)" : "transparent",
                      color: selectedPriorities.length >= 3 ? "rgba(0,0,0,0.3)" : T.ink,
                      cursor: selectedPriorities.length >= 3 ? "not-allowed" : "pointer",
                      transition: "all 0.15s",
                      opacity: selectedPriorities.length >= 3 ? 0.5 : 1
                    }}
                  >
                    + Other
                  </button>
                </div>
                {showCustomPriority && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      value={customPriority}
                      onChange={e => setCustomPriority(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && addCustomPriority()}
                      placeholder="Enter custom priority"
                      autoFocus
                      style={{
                        flex: 1,
                        fontFamily: sans,
                        fontSize: type.ui,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1.5px solid rgba(0,0,0,0.15)",
                        background: "rgba(255,255,255,0.65)",
                        color: T.ink,
                        outline: "none"
                      }}
                    />
                    <button
                      onClick={addCustomPriority}
                      disabled={!customPriority.trim()}
                      style={{
                        fontFamily: sans,
                        fontSize: type.ui,
                        fontWeight: 600,
                        padding: "8px 16px",
                        borderRadius: 8,
                        border: "none",
                        background: customPriority.trim() ? lStyle.accent : "rgba(0,0,0,0.1)",
                        color: customPriority.trim() ? T.white : "rgba(0,0,0,0.3)",
                        cursor: customPriority.trim() ? "pointer" : "not-allowed",
                        transition: "all 0.15s"
                      }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => { setShowCustomPriority(false); setCustomPriority(""); }}
                      style={{
                        fontFamily: sans,
                        fontSize: type.ui,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.15)",
                        background: "transparent",
                        color: T.ink3,
                        cursor: "pointer"
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 0 }}>
                <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                  Desired experience tone
                </label>
                <input
                  value={experienceTone}
                  onChange={e => handleToneChange(e.target.value)}
                  placeholder="e.g. calm and professional, warm and playful, minimal and precise, bold and energizing"
                  style={{
                    width: "100%",
                    fontFamily: serif,
                    fontSize: type.ui,
                    color: T.ink,
                    background: "rgba(255,255,255,0.65)",
                    border: "1.5px solid rgba(0,0,0,0.12)",
                    borderRadius: 8,
                    padding: "12px 16px",
                    outline: "none",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              {/* Brand Positioning Assistant */}
              <div style={{ marginTop: 24, padding: "20px 24px", background: "rgba(74,124,98,0.06)", border: "1.5px solid rgba(74,124,98,0.15)", borderRadius: 12 }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 4 }}>
                    ✨ Brand Positioning Guidance
                  </div>
                  <div style={{ fontFamily: sans, fontSize: 14, color: T.ink3, lineHeight: 1.6, marginBottom: 12 }}>
                    Discover the visual blind spot in your brand space. Copy this guided prompt and paste it into Claude or ChatGPT to get AI-powered insights on distinctive visual territories.
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={copyBrandPositioningPrompt}
                    disabled={!fields.context || fields.context.trim().length < 50}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: (!fields.context || fields.context.trim().length < 50) ? "rgba(0,0,0,0.1)" : T.cream,
                      color: (!fields.context || fields.context.trim().length < 50) ? "rgba(0,0,0,0.4)" : T.ink,
                      cursor: (!fields.context || fields.context.trim().length < 50) ? "not-allowed" : "pointer",
                      transition: "all 0.15s",
                      flex: 1
                    }}
                  >
                    {brandPositioningCopied ? "Copied ✓" : "Copy prompt"}
                  </button>
                  <button
                    onClick={openBrandPositioningInClaude}
                    disabled={!fields.context || fields.context.trim().length < 50}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "none",
                      background: (!fields.context || fields.context.trim().length < 50) ? "rgba(0,0,0,0.1)" : lStyle.accent,
                      color: (!fields.context || fields.context.trim().length < 50) ? "rgba(0,0,0,0.4)" : T.white,
                      cursor: (!fields.context || fields.context.trim().length < 50) ? "not-allowed" : "pointer",
                      transition: "all 0.15s",
                      flex: 1
                    }}
                  >
                    {brandPositioningCopied ? "Copied! Now paste in Claude ✓" : "Open in Claude →"}
                  </button>
                </div>

                {(!fields.context || fields.context.trim().length < 50) && (
                  <div style={{ marginTop: 12, fontFamily: sans, fontSize: 13, color: T.ink4, fontStyle: "italic" }}>
                    Complete the Context layer first to generate the prompt.
                  </div>
                )}
              </div>

              {/* Visual Assets Recommendations */}
              <div style={{ marginTop: 24, padding: "20px 24px", background: "rgba(74,124,98,0.06)", border: "1.5px solid rgba(74,124,98,0.15)", borderRadius: 12 }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 700, letterSpacing: "0.12em", color: lStyle.accent, textTransform: "uppercase", marginBottom: 4 }}>
                    🎨 Visual Assets to Commission
                  </div>
                  <div style={{ fontFamily: sans, fontSize: 14, color: T.ink3, lineHeight: 1.6, marginBottom: 12 }}>
                    Copy this guided prompt and paste it into Claude or ChatGPT to get recommendations for custom visual assets (icons, illustrations, photography, animations, etc.) that make your product distinctive.
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={copyVisualAssetsPrompt}
                    disabled={(!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: ((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.1)" : T.cream,
                      color: ((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.4)" : T.ink,
                      cursor: ((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) ? "not-allowed" : "pointer",
                      transition: "all 0.15s",
                      flex: 1
                    }}
                  >
                    {visualAssetsCopied ? "Copied ✓" : "Copy prompt"}
                  </button>
                  <button
                    onClick={openVisualAssetsInClaude}
                    disabled={(!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "none",
                      background: ((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.1)" : lStyle.accent,
                      color: ((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) ? "rgba(0,0,0,0.4)" : T.white,
                      cursor: ((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) ? "not-allowed" : "pointer",
                      transition: "all 0.15s",
                      flex: 1
                    }}
                  >
                    {visualAssetsCopied ? "Copied! Now paste in Claude ✓" : "Open in Claude →"}
                  </button>
                </div>

                {((!fields.context || fields.context.trim().length < 50) || (!fields.direction || fields.direction.trim().length < 30)) && (
                  <div style={{ marginTop: 12, fontFamily: sans, fontSize: 13, color: T.ink4, fontStyle: "italic" }}>
                    Complete Context and Direction layers first to generate the prompt.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div style={{ padding: "20px 36px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid rgba(0,0,0,0.08)` }}>
          <button
            onClick={() => layerIdx > 0 && setLayerIdx(i => i - 1)}
            disabled={layerIdx === 0}
            style={{
              fontFamily: sans,
              fontSize: type.ui,
              color: layerIdx === 0 ? "rgba(26,28,24,0.38)" : "rgba(26,28,24,0.55)",
              background: "transparent",
              border: "none",
              cursor: layerIdx === 0 ? "not-allowed" : "pointer",
              padding: 0
            }}>
            ← Previous
          </button>
          {layerIdx < LAYERS.length - 1 ? (
            <button
              onClick={() => { setLayerIdx(i => i + 1); setShowExample(false); }}
              disabled={!currentFilled}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                color: currentFilled ? T.white : "rgba(26,28,24,0.5)",
                fontWeight: 500,
                border: "none",
                borderRadius: 8,
                padding: "10px 20px",
                cursor: currentFilled ? "pointer" : "not-allowed",
                background: currentFilled ? lStyle.accent : "rgba(0,0,0,0.1)",
                transition: "all 0.15s"
              }}
            >
              Next layer →
            </button>
          ) : (
            <button
              onClick={() => setPreview(true)}
              disabled={!allFilled}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                color: allFilled ? T.white : "rgba(26,28,24,0.5)",
                fontWeight: 500,
                border: "none",
                borderRadius: 8,
                padding: "10px 20px",
                cursor: allFilled ? "pointer" : "not-allowed",
                background: allFilled ? lStyle.accent : "rgba(0,0,0,0.1)",
                transition: "all 0.15s"
              }}
            >
              Preview prompt →
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div style={{ display: "flex", gap: 8, marginTop: 24, justifyContent: "center" }}>
        {LAYERS.map((l, i) => (
          <div key={l.key} style={{ width: fields[l.key].trim() ? 24 : 6, height: 4, borderRadius: 2, background: i === layerIdx ? T.terra : fields[l.key].trim() ? T.moss : "rgba(255,255,255,0.15)", transition: "all 0.2s" }} />
        ))}
      </div>

      {/* Design Assistant Full Screen */}
      {designAssistantOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: T.cream, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ background: L.references.accent, padding: "28px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <div style={{ maxWidth: 680 }}>
              <div style={{ fontFamily: serif, fontSize: 28, color: T.white, marginBottom: 6 }}>Design Assistant</div>
              <div style={{ fontFamily: sans, fontSize: 15, color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}>Design Steward AI — critique, score, improve. Toggle Research mode for evidence-backed recommendations.</div>
            </div>
            <button
              onClick={() => {
                setDesignAssistantOpen(false);
                setDesignAssistantInput("");
                setDesignAssistantImage(null);
                setDesignAssistantAi({ r: "", l: false });
                setDesignAssistantResearchMode(false);
              }}
              style={{
                background: T.white,
                color: L.references.accent,
                border: "none",
                borderRadius: 8,
                padding: "12px 24px",
                fontSize: 15,
                cursor: "pointer",
                fontFamily: sans,
                fontWeight: 600,
                transition: "all 0.15s",
                flexShrink: 0
              }}
              onMouseOver={e => e.currentTarget.style.background = "rgba(255,255,255,0.9)"}
              onMouseOut={e => e.currentTarget.style.background = T.white}
            >
              ← Close
            </button>
          </div>

          {/* Scrollable Content */}
          <div style={{ overflow: "auto", flexGrow: 1, padding: "48px 40px 80px" }}>
            <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", flexDirection: "column", gap: 28 }}>
              <div>
                <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", textTransform: "uppercase", color: T.ink3, marginBottom: 12 }}>Screenshot or UI (optional)</div>
                {designAssistantImage ? (
                  <div style={{ border: `2px solid ${L.references.accent}`, borderRadius: 12, padding: 32, textAlign: "center", background: L.references.pale }}>
                    <img src={designAssistantImage} alt="Screenshot" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8, display: "block", margin: "0 auto 16px" }} />
                    <button onClick={() => setDesignAssistantImage(null)} style={{ background: T.white, color: L.references.accent, border: `1px solid ${L.references.accent}`, borderRadius: 8, padding: "8px 18px", fontSize: 14, cursor: "pointer", fontFamily: sans }}>Remove</button>
                  </div>
                ) : (
                  <div>
                    <input type="file" accept="image/*" onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => setDesignAssistantImage(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }} style={{ display: "none" }} id="screenshot-upload" />
                    <label htmlFor="screenshot-upload" style={{ display: "block", border: `2px dashed ${L.references.accent}`, borderRadius: 12, padding: 32, textAlign: "center", cursor: "pointer", background: L.references.pale, transition: "all 0.15s" }}>
                      <div style={{ fontFamily: sans, fontSize: 17, color: T.ink, marginBottom: 10 }}>Click to upload a screenshot</div>
                      <div style={{ fontFamily: sans, fontSize: 14, color: T.ink3 }}>PNG, JPG, or other image file</div>
                    </label>
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", textTransform: "uppercase", color: T.ink3, marginBottom: 12 }}>Prompt, concept, or context (optional)</div>
                <textarea value={designAssistantInput} onChange={e => setDesignAssistantInput(e.target.value)} placeholder="Add context: your prompt, brief, or a design question (colors, typography, layout, flows, accessibility, etc.). Or use 'Review my current work' to load your brief and direction."
                  rows={6} style={{ width: "100%", border: `1.5px solid ${T.rule}`, borderRadius: 12, padding: "16px 20px", fontSize: 16, fontFamily: sans, color: T.ink2, background: T.white, resize: "vertical", lineHeight: 1.65, outline: "none", boxSizing: "border-box" }}
                />
                <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, cursor: "pointer", fontFamily: sans, fontSize: 15, color: T.ink2 }}>
                  <input type="checkbox" checked={designAssistantResearchMode} onChange={e => setDesignAssistantResearchMode(e.target.checked)} style={{ width: 18, height: 18 }} />
                  <span>Research & evidence mode — research-backed recommendations, citations, and alternative directions</span>
                </label>
                <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                  <button onClick={fillDesignAssistantFromWork} style={{ background: T.white, border: `1.5px solid ${T.rule}`, color: T.ink2, borderRadius: 8, padding: "12px 20px", fontSize: 15, cursor: "pointer", fontFamily: sans, fontWeight: 600 }}>
                    ↑ Review my current work
                  </button>
                  <button onClick={callDesignSteward} disabled={designAssistantAi.l} style={{ background: designAssistantAi.l ? "rgba(106,78,113,0.3)" : L.references.accent, color: T.white, border: "none", borderRadius: 8, padding: "12px 24px", fontSize: 16, cursor: designAssistantAi.l ? "default" : "pointer", fontFamily: sans, fontWeight: 600 }}>
                    {designAssistantAi.l ? "Reviewing…" : "✦ Get Design Steward review"}
                  </button>
                </div>
              </div>
              {designAssistantAi.l && (
                <div style={{ fontFamily: sans, fontSize: 16, color: T.ink3, fontStyle: "italic", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ display: "inline-block", width: 20, height: 20, border: `2px solid ${L.references.accent}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  {designAssistantResearchMode ? "Researching evidence, assessing design, and preparing recommendations…" : "Assessing design quality, risks, and recommendations…"}
                </div>
              )}
              {designAssistantAi.r && !designAssistantAi.l && (
                <div style={{ background: T.white, borderRadius: 12, padding: "32px 36px", border: `1.5px solid ${T.rule}` }}>
                  <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", textTransform: "uppercase", color: L.references.accent, marginBottom: 20 }}>Design Steward review</div>
                  <div style={{ fontFamily: sans, fontSize: 17, color: T.ink2, lineHeight: 1.75, whiteSpace: "pre-wrap", marginBottom: 24 }}>{designAssistantAi.r}</div>

                  {/* Integration Button */}
                  <button
                    onClick={() => {
                      // Smart targeting - append to the weakest/shortest layer
                      const layerKeys = ['context', 'references', 'direction', 'system', 'scope', 'success'];
                      const feedback = `\n\n[Design Steward Review]\n${designAssistantAi.r}`;

                      // Find the shortest layer to append to
                      const targetLayer = layerKeys.reduce((min, key) =>
                        (!fields[min] || fields[key].length < fields[min].length) ? key : min
                      );

                      setFields(f => ({
                        ...f,
                        [targetLayer]: f[targetLayer].trim() ? `${f[targetLayer]}${feedback}` : designAssistantAi.r
                      }));

                      // Close the modal and clear
                      setDesignAssistantOpen(false);
                      setDesignAssistantInput("");
                      setDesignAssistantImage(null);
                      setDesignAssistantAi({ r: "", l: false });
                      setDesignAssistantResearchMode(false);
                    }}
                    style={{
                      fontFamily: sans,
                      fontSize: type.ui,
                      fontWeight: 600,
                      padding: "12px 24px",
                      borderRadius: 8,
                      border: "none",
                      background: L.references.accent,
                      color: T.white,
                      cursor: "pointer",
                      transition: "all 0.15s"
                    }}
                  >
                    ✓ Integrate Design Steward review to my work
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BUILD STEP ───────────────────────────────────────────────
// BuildStep component removed — functionality merged into Preview (Prompt Anatomy step)
// Steering Formula, demo loader, and complete prompt copy are now part of the Preview section

// ─── REFLECT STEP ─────────────────────────────────────────────
function ReflectStep({ name, onDone }) {
  const [built, setBuilt] = useState("");
  const [surprised, setSurprised] = useState("");
  const [next, setNext] = useState("");
  const [workshopData, setWorkshopData] = useState(null);
  const [reflectionAssessment, setReflectionAssessment] = useState(null);
  const [assessingReflection, setAssessingReflection] = useState(false);

  useEffect(() => {
    async function loadData() {
      const data = await loadParticipantData(name);
      if (data) {
        setWorkshopData(data);
      }
    }
    loadData();
  }, [name]);

  async function getReflectionAssessment() {
    if (!built.trim() || !surprised.trim()) {
      alert("Please complete 'What did you build?' and 'What surprised you?' before getting assessment.");
      return;
    }

    setAssessingReflection(true);

    const intention = workshopData?.intention || {};
    const promptLayers = workshopData?.prompt_layers || {};

    const promptText = `
PARTICIPANT: ${name}

WORKSHOP JOURNEY:

**Their Original Intention:**
What: ${intention.what || "Not captured"}
Who: ${intention.who || "Not captured"}

**Their Prompt Anatomy (5 layers):**
Context: ${promptLayers.context || "Not completed"}
Quality References: ${promptLayers.references || "Not completed"}
Design Direction: ${promptLayers.direction || "Not completed"}
Design System: ${promptLayers.system || "Not completed"}
Scope: ${promptLayers.scope || "Not completed"}
Success: ${promptLayers.success || "Not completed"}

**Their Reflections:**
What they built: ${built}
What surprised them: ${surprised}
What they'd try next: ${next || "Not yet answered"}

---

TASK: Assess this participant's learning journey and growth. This is the END of the workshop — provide a focused learning reflection.

1. **PROGRESS MADE** — What specific growth do you see from intention → prompt → build → reflection? Name 2-3 concrete developments in their thinking.

2. **WHAT TO DO MORE OF** — Based on their entire journey, identify 3 specific practices, mindsets, or approaches they should continue developing. Be concrete and actionable.

Keep it warm, specific, and honest. Max 200 words.

FORMAT:
**Progress Made:**
[2-3 specific growths, each as a separate line]

**What to Do More Of:**
1. [Specific practice]
2. [Specific practice]
3. [Specific practice]
`;

    try {
      const response = await callAI(
        "You are an AI design educator reflecting on a participant's learning journey. Provide a focused, actionable learning reflection. Be warm, specific, and honest. Use the exact format requested.",
        promptText,
        300
      );
      setReflectionAssessment(response);
    } catch (error) {
      setReflectionAssessment("Error generating reflection assessment. Please try again.");
    }

    setAssessingReflection(false);
  }

  function saveAndDone() {
    saveParticipantData(name, "reflection", { built, surprised, next });
    onDone();
  }

  async function downloadWork() {
    // Reload latest data from database to ensure we have all recent updates
    const latestData = await loadParticipantData(name);
    const intention = latestData?.intention || workshopData?.intention || {};
    const promptLayers = latestData?.prompt_layers || workshopData?.prompt_layers || {};

    let content = `DESIGN & BUILD WITH AI — WORKSHOP NOTES\n`;
    content += `Name: ${name}\n`;
    content += `Date: ${new Date().toLocaleDateString()}\n`;
    content += `\n${"=".repeat(60)}\n\n`;

    // Intention
    content += `STEP 1: INTENTION\n\n`;
    content += `What:\n${intention.what || "—"}\n\n`;
    content += `Who:\n${intention.who || "—"}\n\n`;
    content += `Why now:\n${intention.why || "—"}\n\n`;
    content += `${"=".repeat(60)}\n\n`;

    // Prompt Layers (5 layers total after combining Scope + Success)
    content += `STEP 2: PROMPT ANATOMY\n\n`;
    LAYERS.forEach(layer => {
      if (layer.combined) {
        // Handle combined Scope + Success layer
        content += `${layer.num}. SCOPE\n`;
        content += `${promptLayers.scope || "—"}\n\n`;
        content += `${layer.num}. SUCCESS\n`;
        content += `${promptLayers.success || "—"}\n\n`;
      } else {
        const layerData = promptLayers[layer.key] || "—";
        content += `${layer.num}. ${L[layer.key].label.toUpperCase()}\n`;
        content += `${layerData}\n\n`;
      }
    });
    content += `${"=".repeat(60)}\n\n`;

    // Reflection
    content += `STEP 3: REFLECTION\n\n`;
    content += `What I built:\n${built || "—"}\n\n`;
    content += `What surprised me:\n${surprised || "—"}\n\n`;
    content += `What I'd try next:\n${next || "—"}\n\n`;
    content += `${"=".repeat(60)}\n\n`;
    content += `Generated with Wonderpath Studio · Design & Build With AI\n`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name.replace(/\s+/g, "-")}_workshop_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.15em", color: T.terra, marginBottom: 16, textTransform: "uppercase" }}>Step 03</div>
      <h2 style={{ fontFamily: serif, fontSize: type.h1, fontWeight: 400, color: T.cream, margin: "0 0 12px" }}>What did you make?</h2>
      <p style={{ fontFamily: sans, fontSize: type.ui, color: T.white, margin: "0 0 48px", lineHeight: 1.6 }}>Three minutes. Three questions.</p>

      <div style={{ background: T.cream, borderRadius: 16, padding: "36px 40px", display: "flex", flexDirection: "column", gap: 28 }}>
        <div>
          <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>What did you build?</label>
          <Textarea value={built} onChange={setBuilt} placeholder="Describe what you made — what it is, what it does, what it looks like." rows={3} />
        </div>
        <div>
          <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>What surprised you?</label>
          <Textarea value={surprised} onChange={setSurprised} placeholder="About the process, the output, yourself..." rows={3} />
        </div>
        <div>
          <label style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", display: "block", marginBottom: 8, textTransform: "uppercase" }}>What would you try next?</label>
          <Textarea value={next} onChange={setNext} placeholder="If you had another session, where would you take this?" rows={3} />
        </div>
      </div>

      {/* Reflection Assessment Section */}
      {built.trim() && surprised.trim() && (
        <div style={{ marginTop: 40 }}>
          <div style={{ background: "rgba(196,154,42,0.08)", border: "1.5px solid rgba(196,154,42,0.2)", borderRadius: 12, padding: "20px 24px", marginBottom: 24 }}>
            <div style={{ fontFamily: sans, fontSize: type.ui, fontWeight: 600, color: T.terra, marginBottom: 4 }}>
              ✨ See your learning journey
            </div>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.white, lineHeight: 1.6 }}>
              Get AI reflection on your progress, a response to what surprised you, and practices to continue developing.
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
            <button
              onClick={getReflectionAssessment}
              disabled={assessingReflection}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                fontWeight: 600,
                padding: "12px 24px",
                borderRadius: 8,
                border: "none",
                background: assessingReflection ? "rgba(0,0,0,0.1)" : T.terra,
                color: assessingReflection ? "rgba(255,255,255,0.4)" : T.white,
                cursor: assessingReflection ? "not-allowed" : "pointer",
                transition: "all 0.15s"
              }}
            >
              {assessingReflection ? "Reflecting on your journey..." : reflectionAssessment ? "↻ Refresh learning reflection" : "✨ Get learning reflection"}
            </button>
          </div>

          {reflectionAssessment && (
            <div style={{ background: T.cream, borderRadius: 12, padding: "28px 32px", marginBottom: 32 }}>
              <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.1em", color: T.terra, marginBottom: 16, textTransform: "uppercase" }}>
                Your Learning Journey
              </div>
              <div style={{ fontFamily: sans, fontSize: 15, color: T.ink2, lineHeight: 1.7, marginBottom: 20 }}>
                {reflectionAssessment.split('\n').map((line, i) => {
                  // Parse **bold** markdown
                  const parts = line.split(/(\*\*[^*]+\*\*)/g);
                  return (
                    <div key={i} style={{ marginBottom: i < reflectionAssessment.split('\n').length - 1 ? 8 : 0 }}>
                      {parts.map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={j} style={{ fontWeight: 600, color: T.ink }}>{part.slice(2, -2)}</strong>;
                        }
                        return <span key={j}>{part}</span>;
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Integrate Button */}
              <button
                onClick={() => {
                  // Append to the "next" field as it's about future learning
                  setNext(prev => prev.trim() ? `${prev}\n\n[Learning Reflection]\n${reflectionAssessment}` : reflectionAssessment);
                  setReflectionAssessment(null);
                }}
                style={{
                  fontFamily: sans,
                  fontSize: type.ui,
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: T.terra,
                  color: T.white,
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
              >
                ✓ Integrate learning reflection to my notes for download
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, marginTop: 48 }}>
        <Btn variant="primary" onClick={saveAndDone} disabled={!built.trim()}>Submit my reflection →</Btn>
        <Btn variant="ghost" onClick={downloadWork}>Download my notes</Btn>
      </div>
    </div>
  );
}

// ─── WORKSHOP SCREEN ──────────────────────────────────────────
function WorkshopScreen({ name, onDone, userApiConfig, onRequestApiSetup }) {
  const [step, setStep] = useState("intention");
  const [intention, setIntention] = useState(null);
  const [promptFields, setPromptFields] = useState(null);
  const [demoLoaded, setDemoLoaded] = useState(false);

  // Check if user has API config, if not show modal on first AI use
  // eslint-disable-next-line no-unused-vars
  const checkApiSetup = () => {
    if (!userApiConfig || userApiConfig.provider === "facilitator") {
      // User hasn't set up their own key yet, show modal
      onRequestApiSetup();
      return false;
    }
    return true;
  };

  function loadAllDemoData() {
    // Set intention data
    setIntention({
      what: "Seasonal plant subscription service with curated monthly boxes",
      who: "Urban apartment dwellers in their 30s-40s who want to bring nature indoors but feel overwhelmed by plant care"
    });

    // Set prompt fields data
    setPromptFields({
      context: "I'm building a homepage for GreenSeason, a seasonal plant delivery service that curates climate-appropriate plants for your region. The user is an urban apartment dweller (30s-40s) browsing on mobile during their lunch break, feeling stressed and disconnected from nature. They've tried houseplants before but they died — they want someone to take the guesswork out of plant selection and care. They're scrolling Instagram when they see a friend's beautiful plant setup from GreenSeason.\n\nPRIMARY USER ACTION: Browse the current season's curated plant box, understand what's included (plant + care guide + seasonal tips), see pricing, and add to cart or subscribe monthly.",

      references: "Reference 1: https://www.headspace.com\nWhat stands out: Calming color palette, clear benefit statements, reduces anxiety about getting started, friendly illustrations that don't talk down to users\n\nReference 2: https://www.sweetgreen.com\nWhat stands out: Seasonal rotation highlighted visually, farm-to-table trust signals, mobile-first card layouts, photography that makes you crave the product\n\nReference 3: https://www.notion.so\nWhat stands out: Progressive disclosure of complexity, clear information hierarchy, generous whitespace, confident use of color without overwhelming",

      direction: "This should feel like opening a window in spring — fresh, alive, optimistic but grounded. Not precious or intimidating like a boutique florist. Not clinical like a gardening catalog. Think: your most organized friend who always has thriving plants is sharing their secret with you.\n\nKey design priorities: Trustworthy, Calming, Educational\n\nExperience tone: The user should feel capable and excited, not anxious. Like they're being let in on knowledge that was gatekept before. Every element should reduce decision fatigue.",

      system: "Colors: sage #7A9B76 · clay #D4A574 · cream #FAF7F2\nType: Fraunces headings 36px · Inter body 16px\n\nDesign system principles:\n1. Establish clear visual hierarchy that guides users from seasonal context → product → action, using size, color, and spacing to create natural reading flow\n2. Maintain WCAG AA contrast ratios minimum (sage on cream = 4.8:1, clay on cream = 4.2:1) with larger text sizes to ensure accessibility\n3. Use consistent spacing scale (8px base unit) to create rhythm and predictability across all components\n4. Implement semantic HTML structure (header, main, section, article) for screen reader navigation\n5. Design components mobile-first with touch targets minimum 44×44px and adequate spacing between interactive elements",

      scope: "Build the hero section + seasonal plant box card only.\n\nHero section: Season badge (\"Spring 2025\"), main headline, 2-sentence value prop, ambient plant photo background with overlay for text legibility.\n\nPlant box card: Product photo, box name (\"Spring Awakening Box\"), what's included (3 plants with names), care level indicator, price ($68), \"Add to cart\" button + \"Learn more\" link.\n\nMobile-first, semantic HTML, one-column layout. No navigation, footer, or checkout flow.",

      success: "A busy professional opens this on their phone and within 3 seconds understands: (1) what season we're in, (2) what they're getting (plants + guidance), (3) how much it costs. They feel calm looking at it, not stressed. The design feels like a calm Sunday morning, not a sales pitch. The text is readable without zooming. The add-to-cart button is impossible to miss but doesn't scream. They can imagine this box on their windowsill."
    });

    setDemoLoaded(true);
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg }}>
      <StepNav current={step} name={name} onStepChange={setStep} userApiConfig={userApiConfig} onRequestApiSetup={onRequestApiSetup} />
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "120px 32px 100px" }}>
        {step === "intention" && (
          <IntentionStep
            name={name}
            onNext={(data) => { setIntention(data); setStep("anatomy"); }}
            initialData={intention}
            loadDemoData={loadAllDemoData}
            demoLoaded={demoLoaded}
          />
        )}
        {step === "anatomy" && (
          <AnatomyStep
            name={name}
            onNext={(fields) => { setPromptFields(fields); setStep("reflect"); }}
            initialData={promptFields}
            loadDemoData={loadAllDemoData}
            demoLoaded={demoLoaded}
            userApiConfig={userApiConfig}
            onRequestApiSetup={onRequestApiSetup}
          />
        )}
        {step === "reflect" && (
          <ReflectStep name={name} onDone={onDone} />
        )}
      </div>
    </div>
  );
}

// ─── FACILITATOR SCREEN ───────────────────────────────────────
function FacilitatorScreen({ onExit }) {
  const [participants, setParticipants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [code, setCode] = useState("");
  const [tab, setTab] = useState("live");
  const [assessments, setAssessments] = useState({});
  const [assessingAll, setAssessingAll] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sessionExpiration, setSessionExpiration] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    async function init() {
      const [p, c] = await Promise.all([getAllParticipants(), getSessionCode()]);
      setParticipants(p);
      setCode(c);
      if (p.length > 0 && !selected) {
        setSelected(p[0].name);
      }
    }
    init();
    const iv = setInterval(() => {
      getAllParticipants().then(p => {
        setParticipants(p);
        if (p.length > 0 && !selected) {
          setSelected(p[0].name);
        }
      });
    }, 15000);
    return () => clearInterval(iv);
  }, [selected]);

  async function assessAllParticipants() {
    setAssessingAll(true);
    const newAssessments = {};
    for (const p of participants) {
      const assessment = await assessParticipant(p);
      newAssessments[p.name] = assessment;
    }
    setAssessments(newAssessments);
    setAssessingAll(false);
  }

  async function assessSingleParticipant(participantData) {
    const assessment = await assessParticipant(participantData);
    setAssessments(prev => ({ ...prev, [participantData.name]: assessment }));
  }

  const selectedData = participants.find(p => p.name === selected);

  async function handleDeleteParticipant(name) {
    if (!window.confirm(`Delete ${name}'s data? This cannot be undone.`)) {
      return;
    }
    const success = await deleteParticipant(name);
    if (success) {
      const updated = await getAllParticipants();
      setParticipants(updated);
      if (selected === name) {
        setSelected(updated.length > 0 ? updated[0].name : null);
      }
    }
  }

  // Load session expiration date
  useEffect(() => {
    async function loadExpiration() {
      try {
        const sessionCode = await getSessionCode();
        // eslint-disable-next-line no-unused-vars
        const { data, error } = await supabase
          .from("sessions")
          .select("expires_at")
          .eq("code", sessionCode)
          .maybeSingle();
        if (data && data.expires_at) {
          // Format date for input (YYYY-MM-DD)
          const date = new Date(data.expires_at);
          const formatted = date.toISOString().split('T')[0];
          setSessionExpiration(formatted);
        }
      } catch (e) {
        console.error("Load expiration error:", e);
      }
    }
    loadExpiration();
  }, [code]);

  async function rotateSessionCode() {
    if (!window.confirm("Rotate session code? This will prevent new participants from joining with the old code. Existing participants will not be affected.")) {
      return;
    }

    const newCode = `BUILD${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;

    try {
      await setSessionCode(newCode);
      setCode(newCode);
      alert(`Session code rotated to: ${newCode}`);
    } catch (e) {
      console.error("Rotate code error:", e);
      alert("Failed to rotate session code. Please try again.");
    }
  }

  async function saveSessionSettings() {
    setSavingSettings(true);

    try {
      const updates = {};

      if (sessionExpiration) {
        // Convert to ISO timestamp
        const date = new Date(sessionExpiration);
        date.setHours(23, 59, 59, 999); // End of day
        updates.expires_at = date.toISOString();
      } else {
        updates.expires_at = null;
      }

      await supabase
        .from("sessions")
        .update(updates)
        .eq("code", code);

      alert("Session settings saved successfully!");
      setSettingsOpen(false);
    } catch (e) {
      console.error("Save settings error:", e);
      alert("Failed to save settings. Please try again.");
    }

    setSavingSettings(false);
  }

  return (
    <div style={{ minHeight: "100vh", background: "#2d4552" }}>
      {/* App bar header */}
      <div style={{ display: "flex", alignItems: "center", padding: "14px 28px", borderBottom: "1px solid rgba(255,255,255,0.07)", background: "#1c2a35" }}>
        <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.85)", letterSpacing: "0.06em", marginRight: "auto" }}>
          Design and Build with AI
        </div>
        <div style={{ display: "flex", gap: 28, marginRight: "auto", marginLeft: 48 }}>
          <span style={{ fontFamily: sans, fontSize: 12, color: "rgba(255,255,255,0.85)", letterSpacing: "0.05em", background: "rgba(255,255,255,0.08)", padding: "4px 12px", borderRadius: 6 }}>
            FACILITATOR
          </span>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => setSettingsOpen(true)}
            style={{
              fontFamily: sans,
              fontSize: 12,
              color: "rgba(255,255,255,0.85)",
              background: "rgba(255,255,255,0.08)",
              border: "none",
              borderRadius: 6,
              padding: "6px 14px",
              cursor: "pointer",
              fontWeight: 500,
              transition: "all 0.15s"
            }}
            onMouseOver={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
            onMouseOut={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
          >
            ⚙ SETTINGS
          </button>
          <button
            onClick={onExit}
            style={{
              fontFamily: sans,
              fontSize: 12,
              color: T.white,
              background: "#b85a5a",
              border: "none",
              borderRadius: 6,
              padding: "6px 14px",
              cursor: "pointer",
              fontWeight: 500,
              transition: "all 0.15s"
            }}
          >
            ← PARTICIPANT VIEW
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 40, padding: "0 60px", borderBottom: "1px solid rgba(255,255,255,0.07)", background: "#2d4552" }}>
        <button
          onClick={() => setTab("live")}
          style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 500,
            color: tab === "live" ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)",
            background: "none",
            border: "none",
            borderBottom: tab === "live" ? "2px solid #d4a574" : "2px solid transparent",
            padding: "18px 0",
            cursor: "pointer",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            transition: "all 0.2s"
          }}
        >
          LIVE
        </button>
        <button
          onClick={() => setTab("code")}
          style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 500,
            color: tab === "code" ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)",
            background: "none",
            border: "none",
            borderBottom: tab === "code" ? "2px solid #d4a574" : "2px solid transparent",
            padding: "18px 0",
            cursor: "pointer",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            transition: "all 0.2s"
          }}
        >
          CODE
        </button>
        <button
          onClick={() => setTab("insights")}
          style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 500,
            color: tab === "insights" ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)",
            background: "none",
            border: "none",
            borderBottom: tab === "insights" ? "2px solid #d4a574" : "2px solid transparent",
            padding: "18px 0",
            cursor: "pointer",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            transition: "all 0.2s"
          }}
        >
          INSIGHTS
        </button>
      </div>

      {/* Main content */}
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "60px 40px" }}>
        {tab === "live" && (
          <>
            {/* Title */}
            <h1 style={{ fontFamily: serif, fontSize: 42, fontWeight: 400, color: "rgba(255,255,255,0.95)", margin: "0 0 8px", lineHeight: 1.2 }}>
              Live session view
            </h1>
            <p style={{ fontFamily: sans, fontSize: 14, color: "rgba(255,255,255,0.5)", margin: "0 0 40px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              {participants.length} participant{participants.length !== 1 ? "s" : ""} · refreshes every 15s
            </p>

            {/* Participant pills */}
            {participants.length === 0 ? (
              <div style={{ fontFamily: sans, fontSize: type.ui, color: "rgba(255,255,255,0.5)", fontStyle: "italic", marginBottom: 40 }}>
                No participants yet. Share session code <span style={{ color: T.gold, fontWeight: 600, letterSpacing: "0.1em" }}>{code}</span> to get started.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 40 }}>
                {participants.map(p => {
                  const assessment = assessments[p.name];
                  const statusColors = {
                    excelling: "#7eb89a",
                    "on-track": "#6b9ad6",
                    "needs-attention": "#d4a574",
                    stuck: "#b85a5a",
                    unknown: "rgba(255,255,255,0.2)"
                  };
                  const statusColor = statusColors[assessment?.status] || statusColors.unknown;

                  return (
                    <div
                      key={p.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: selected === p.name ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
                        border: `1px solid ${selected === p.name ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: 8,
                        padding: "10px 14px 10px 12px",
                        transition: "all 0.15s"
                      }}
                    >
                      {/* Status indicator dot */}
                      {assessment && (
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: statusColor,
                            flexShrink: 0
                          }}
                          title={assessment.status}
                        />
                      )}
                      <button
                        onClick={() => setSelected(p.name)}
                        style={{
                          fontFamily: sans,
                          fontSize: 14,
                          color: "rgba(255,255,255,0.85)",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: selected === p.name ? 600 : 400,
                          padding: 0
                        }}
                      >
                        {p.name}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteParticipant(p.name);
                        }}
                        style={{
                          fontFamily: sans,
                          fontSize: 14,
                          color: "rgba(255,255,255,0.4)",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px 6px",
                          lineHeight: 1,
                          transition: "all 0.15s"
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.color = "#b85a5a"; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
                        title={`Delete ${p.name}`}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Selected participant detail */}
            {selectedData && (
              <>
                <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", color: "rgba(255,255,255,0.5)", marginBottom: 32, textTransform: "uppercase" }}>
                  {selectedData.name}'s session
                </div>

                {/* Intention card */}
                {selectedData.intention && (
                  <div style={{ background: T.cream, borderRadius: 12, padding: "28px 32px", marginBottom: 20 }}>
                    <div style={{ background: "#7eb89a", color: T.cream, fontFamily: sans, fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", padding: "12px 20px", margin: "-28px -32px 20px", borderRadius: "12px 12px 0 0", textTransform: "uppercase" }}>
                      Intention
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                      <div>
                        <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>What is this for?</div>
                        <div style={{ fontFamily: sans, fontSize: 15, color: T.ink, lineHeight: 1.6 }}>{selectedData.intention.what || <span style={{ fontStyle: "italic", color: T.ink4 }}>Not answered</span>}</div>
                      </div>
                      {selectedData.intention.who && (
                        <div>
                          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Who is this for?</div>
                          <div style={{ fontFamily: sans, fontSize: 15, color: T.ink, lineHeight: 1.6 }}>{selectedData.intention.who}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Prompt anatomy card */}
                {selectedData.prompt_layers && (
                  <div style={{ background: T.cream, borderRadius: 12, padding: "28px 32px", marginBottom: 20 }}>
                    <div style={{ background: "#a07bc8", color: T.cream, fontFamily: sans, fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", padding: "12px 20px", margin: "-28px -32px 20px", borderRadius: "12px 12px 0 0", textTransform: "uppercase" }}>
                      Prompt anatomy · Six layers
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                      {LAYERS.map(l => {
                        const val = selectedData.prompt_layers[l.key];
                        return (
                          <div key={l.key}>
                            <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: L[l.key].accent, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>
                              {l.num}. {L[l.key].label}
                            </div>
                            <div style={{ fontFamily: sans, fontSize: 14, color: val ? T.ink : T.ink4, lineHeight: 1.6, fontStyle: val ? "normal" : "italic" }}>
                              {val ? (val.length > 180 ? val.substring(0, 180) + "..." : val) : "Not answered"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Reflection card */}
                {selectedData.reflection && (
                  <div style={{ background: T.cream, borderRadius: 12, padding: "28px 32px" }}>
                    <div style={{ background: "#4a7c62", color: T.cream, fontFamily: sans, fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", padding: "12px 20px", margin: "-28px -32px 20px", borderRadius: "12px 12px 0 0", textTransform: "uppercase" }}>
                      Reflection
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20 }}>
                      {selectedData.reflection.built && (
                        <div>
                          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>What did you build?</div>
                          <div style={{ fontFamily: sans, fontSize: 15, color: T.ink, lineHeight: 1.6 }}>{selectedData.reflection.built}</div>
                        </div>
                      )}
                      {selectedData.reflection.surprised && (
                        <div>
                          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>What surprised you?</div>
                          <div style={{ fontFamily: sans, fontSize: 15, color: T.ink, lineHeight: 1.6 }}>{selectedData.reflection.surprised}</div>
                        </div>
                      )}
                      {selectedData.reflection.next && (
                        <div>
                          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>What would you try next?</div>
                          <div style={{ fontFamily: sans, fontSize: 15, color: T.ink, lineHeight: 1.6 }}>{selectedData.reflection.next}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {tab === "code" && (
          <div style={{ fontFamily: sans, fontSize: type.ui, color: "rgba(255,255,255,0.6)", textAlign: "center", padding: "60px 0" }}>
            Code tab — Coming soon
          </div>
        )}

        {tab === "insights" && (
          <>
            {/* Title */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
              <div>
                <h1 style={{ fontFamily: serif, fontSize: 42, fontWeight: 400, color: "rgba(255,255,255,0.95)", margin: "0 0 8px", lineHeight: 1.2 }}>
                  AI Assessment
                </h1>
                <p style={{ fontFamily: sans, fontSize: 14, color: "rgba(255,255,255,0.5)", margin: 0, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Evaluate participant progress and quality
                </p>
              </div>
              <button
                onClick={assessAllParticipants}
                disabled={assessingAll || participants.length === 0}
                style={{
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.white,
                  background: assessingAll ? "rgba(255,255,255,0.1)" : T.gold,
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 24px",
                  cursor: assessingAll || participants.length === 0 ? "not-allowed" : "pointer",
                  opacity: assessingAll || participants.length === 0 ? 0.5 : 1,
                  transition: "all 0.15s"
                }}
              >
                {assessingAll ? "Analyzing..." : "✨ Assess All Participants"}
              </button>
            </div>

            {/* Rubric Overview */}
            <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "24px 28px", marginBottom: 32 }}>
              <h3 style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", color: T.gold, margin: "0 0 20px", textTransform: "uppercase" }}>
                Assessment Rubric
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px 32px" }}>
                {Object.entries(ASSESSMENT_CRITERIA).map(([key, val]) => (
                  <div key={key}>
                    <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.85)", marginBottom: 4, textTransform: "uppercase" }}>
                      {key} <span style={{ color: "rgba(255,255,255,0.5)" }}>({val.weight}%)</span>
                    </div>
                    <div style={{ fontFamily: sans, fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
                      {val.criteria}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Participant Assessments */}
            {participants.length === 0 ? (
              <div style={{ fontFamily: sans, fontSize: type.ui, color: "rgba(255,255,255,0.5)", fontStyle: "italic", textAlign: "center", padding: "60px 0" }}>
                No participants yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {participants.map(p => {
                  const assessment = assessments[p.name];
                  const statusColors = {
                    excelling: "#7eb89a",
                    "on-track": "#6b9ad6",
                    "needs-attention": "#d4a574",
                    stuck: "#b85a5a"
                  };
                  const statusBadges = {
                    excelling: "Excelling ✓",
                    "on-track": "On Track",
                    "needs-attention": "Needs Attention",
                    stuck: "Stuck"
                  };

                  return (
                    <div key={p.name} style={{ background: T.cream, borderRadius: 12, padding: "24px 28px" }}>
                      {/* Header with name and status */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <h3 style={{ fontFamily: serif, fontSize: 24, fontWeight: 400, color: T.ink, margin: 0 }}>
                          {p.name}
                        </h3>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                          {assessment && (
                            <div style={{
                              fontFamily: sans,
                              fontSize: 12,
                              fontWeight: 600,
                              letterSpacing: "0.05em",
                              textTransform: "uppercase",
                              color: T.white,
                              background: statusColors[assessment.status] || "rgba(0,0,0,0.2)",
                              padding: "6px 12px",
                              borderRadius: 6
                            }}>
                              {statusBadges[assessment.status] || assessment.status}
                            </div>
                          )}
                          <button
                            onClick={() => assessSingleParticipant(p)}
                            style={{
                              fontFamily: sans,
                              fontSize: 12,
                              fontWeight: 600,
                              color: T.ink3,
                              background: "transparent",
                              border: `1.5px solid ${T.ink4}`,
                              borderRadius: 6,
                              padding: "6px 12px",
                              cursor: "pointer",
                              transition: "all 0.15s"
                            }}
                          >
                            {assessment ? "Re-assess" : "Assess"}
                          </button>
                        </div>
                      </div>

                      {/* Progress bar */}
                      {assessment && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>
                            Progress: {assessment.completedLayers} / {assessment.totalLayers} layers completed
                          </div>
                          <div style={{ background: "rgba(0,0,0,0.08)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                            <div style={{
                              background: statusColors[assessment.status] || T.ink4,
                              height: "100%",
                              width: `${(assessment.completedLayers / assessment.totalLayers) * 100}%`,
                              transition: "width 0.3s ease"
                            }} />
                          </div>
                        </div>
                      )}

                      {/* AI Analysis */}
                      {assessment ? (
                        <div style={{ fontFamily: sans, fontSize: 14, color: T.ink2, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                          {assessment.analysis}
                        </div>
                      ) : (
                        <div style={{ fontFamily: sans, fontSize: 14, color: T.ink4, fontStyle: "italic" }}>
                          Click "Assess" to analyze this participant's work.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Settings Modal */}
      {settingsOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
          onClick={() => setSettingsOpen(false)}
        >
          <div
            style={{
              background: T.cream,
              borderRadius: 16,
              padding: "32px 40px",
              maxWidth: 540,
              width: "90%",
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)"
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontFamily: serif, fontSize: 28, fontWeight: 400, color: T.ink, margin: "0 0 8px" }}>
                Session Settings
              </h2>
              <p style={{ fontFamily: sans, fontSize: 14, color: T.ink3, margin: 0 }}>
                Manage session code and expiration
              </p>
            </div>

            {/* Current Session Code */}
            <div style={{ marginBottom: 24, padding: "16px 20px", background: "rgba(196,154,42,0.08)", border: "1.5px solid rgba(196,154,42,0.2)", borderRadius: 12 }}>
              <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: T.ink3, letterSpacing: "0.08em", marginBottom: 8, textTransform: "uppercase" }}>
                Current Session Code
              </div>
              <div style={{ fontFamily: serif, fontSize: 32, fontWeight: 600, color: T.terra, letterSpacing: "0.05em" }}>
                {code}
              </div>
            </div>

            {/* Rotate Code Button */}
            <div style={{ marginBottom: 28 }}>
              <button
                onClick={rotateSessionCode}
                style={{
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.white,
                  background: "#b85a5a",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 20px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  width: "100%"
                }}
                onMouseOver={e => e.currentTarget.style.background = "#a04848"}
                onMouseOut={e => e.currentTarget.style.background = "#b85a5a"}
              >
                ↻ Rotate Session Code
              </button>
              <p style={{ fontFamily: sans, fontSize: 13, color: T.ink4, margin: "8px 0 0", lineHeight: 1.5 }}>
                Generates a new code and prevents new participants from joining with the old code. Existing participants are not affected.
              </p>
            </div>

            {/* Expiration Date */}
            <div style={{ marginBottom: 28 }}>
              <label style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: T.ink2, display: "block", marginBottom: 8 }}>
                Session Expiration Date (optional)
              </label>
              <input
                type="date"
                value={sessionExpiration}
                onChange={e => setSessionExpiration(e.target.value)}
                style={{
                  width: "100%",
                  fontFamily: sans,
                  fontSize: 15,
                  color: T.ink,
                  background: T.white,
                  border: "1.5px solid rgba(0,0,0,0.15)",
                  borderRadius: 8,
                  padding: "12px 16px",
                  boxSizing: "border-box",
                  outline: "none"
                }}
              />
              <p style={{ fontFamily: sans, fontSize: 13, color: T.ink4, margin: "8px 0 0", lineHeight: 1.5 }}>
                After this date, participants will not be able to join or access the workshop. Leave blank for no expiration.
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                onClick={() => setSettingsOpen(false)}
                style={{
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.ink3,
                  background: "transparent",
                  border: "1.5px solid rgba(0,0,0,0.15)",
                  borderRadius: 8,
                  padding: "10px 20px",
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveSessionSettings}
                disabled={savingSettings}
                style={{
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.white,
                  background: savingSettings ? "rgba(74,124,98,0.5)" : "#4a7c62",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 20px",
                  cursor: savingSettings ? "not-allowed" : "pointer",
                  transition: "all 0.15s"
                }}
              >
                {savingSettings ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── API KEY SETUP MODAL ──────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function ApiKeyModal({ onClose, onSave }) {
  const [provider, setProvider] = useState("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);

  const handleSave = () => {
    if (apiKey.trim()) {
      localStorage.setItem("userApiProvider", provider);
      localStorage.setItem("userApiKey", apiKey.trim());
      onSave({ provider, apiKey: apiKey.trim() });
      onClose();
    }
  };

  const handleSkip = () => {
    localStorage.setItem("userApiProvider", "facilitator");
    onSave({ provider: "facilitator", apiKey: null });
    onClose();
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.75)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
      padding: 20
    }}>
      <div style={{
        background: T.cream,
        borderRadius: 16,
        maxWidth: 580,
        width: "100%",
        padding: "32px 36px",
        maxHeight: "90vh",
        overflowY: "auto"
      }}>
        {/* Header */}
        <div style={{ fontFamily: sans, fontSize: type.label, letterSpacing: "0.12em", color: T.terra, marginBottom: 8, textTransform: "uppercase" }}>
          🔑 AI Setup
        </div>
        <h2 style={{ fontFamily: serif, fontSize: type.h3, fontWeight: 400, color: T.ink, margin: "0 0 12px" }}>
          Choose your AI provider
        </h2>
        <p style={{ fontFamily: sans, fontSize: 15, color: T.ink2, lineHeight: 1.6, marginBottom: 24 }}>
          To use AI features in this workshop, you can use your own API key (recommended) or use the facilitator's shared key.
        </p>

        {/* Provider Selection */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Select Provider
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { id: "anthropic", name: "Anthropic Claude", color: "#D4845A" },
              { id: "openai", name: "OpenAI GPT-4", color: "#6b9ad6" },
              { id: "facilitator", name: "Use Facilitator's Key", color: "#7eb89a" }
            ].map(p => (
              <button
                key={p.id}
                onClick={() => setProvider(p.id)}
                style={{
                  flex: "1 1 150px",
                  fontFamily: sans,
                  fontSize: 14,
                  fontWeight: provider === p.id ? 600 : 400,
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: `2px solid ${provider === p.id ? p.color : "rgba(0,0,0,0.15)"}`,
                  background: provider === p.id ? `${p.color}15` : "transparent",
                  color: provider === p.id ? p.color : T.ink2,
                  cursor: "pointer",
                  transition: "all 0.15s"
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        {/* API Key Input (only if not facilitator) */}
        {provider !== "facilitator" && (
          <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: sans, fontSize: type.label, fontWeight: 600, color: T.ink, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Your API Key
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  placeholder={provider === "anthropic" ? "sk-ant-api03-..." : "sk-..."}
                  style={{
                    width: "100%",
                    fontFamily: sans,
                    fontSize: 14,
                    padding: "12px 50px 12px 14px",
                    borderRadius: 8,
                    border: "1.5px solid rgba(0,0,0,0.15)",
                    background: T.white,
                    color: T.ink,
                    outline: "none"
                  }}
                />
                <button
                  onClick={() => setShowKey(!showKey)}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontFamily: sans,
                    fontSize: 12,
                    padding: "4px 8px",
                    borderRadius: 4,
                    border: "none",
                    background: "rgba(0,0,0,0.05)",
                    color: T.ink3,
                    cursor: "pointer"
                  }}
                >
                  {showKey ? "Hide" : "Show"}
                </button>
              </div>
              <div style={{ fontFamily: sans, fontSize: 13, color: T.ink3, marginTop: 8, lineHeight: 1.5 }}>
                Your API key is stored locally in your browser only. It's never sent to our servers.
              </div>
            </div>

            {/* Instructions */}
            <div style={{ background: "rgba(126,184,154,0.08)", border: "1.5px solid rgba(126,184,154,0.2)", borderRadius: 8, padding: "16px 18px", marginBottom: 24 }}>
              <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: "#4a7c62", marginBottom: 8 }}>
                💡 How to get your API key
              </div>
              <ol style={{ fontFamily: sans, fontSize: 13, color: T.ink2, lineHeight: 1.6, margin: 0, paddingLeft: 20 }}>
                {provider === "anthropic" ? (
                  <>
                    <li>Go to <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" style={{ color: T.terra, textDecoration: "underline" }}>console.anthropic.com</a></li>
                    <li>Sign up (free $5 credits for new users)</li>
                    <li>Go to API Keys → Create Key</li>
                    <li>Copy and paste above</li>
                  </>
                ) : (
                  <>
                    <li>Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: T.terra, textDecoration: "underline" }}>platform.openai.com/api-keys</a></li>
                    <li>Sign up (free $5 credits for new users)</li>
                    <li>Create new secret key</li>
                    <li>Copy and paste above</li>
                  </>
                )}
              </ol>
              <div style={{ fontFamily: sans, fontSize: 12, color: T.ink3, marginTop: 8, fontStyle: "italic" }}>
                Cost: ~$1-2 for the full workshop
              </div>
            </div>
          </>
        )}

        {provider === "facilitator" && (
          <div style={{ background: "rgba(126,184,154,0.08)", border: "1.5px solid rgba(126,184,154,0.2)", borderRadius: 8, padding: "16px 18px", marginBottom: 24 }}>
            <div style={{ fontFamily: sans, fontSize: 14, color: T.ink2, lineHeight: 1.6 }}>
              You'll use the facilitator's shared API key. AI features may be slower if many people are using them at once.
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          {provider !== "facilitator" && (
            <button
              onClick={handleSkip}
              style={{
                fontFamily: sans,
                fontSize: type.ui,
                padding: "10px 20px",
                borderRadius: 8,
                border: "1.5px solid rgba(0,0,0,0.15)",
                background: "transparent",
                color: T.ink3,
                cursor: "pointer",
                transition: "all 0.15s"
              }}
            >
              Use facilitator's key instead
            </button>
          )}
          <button
            onClick={provider === "facilitator" ? handleSkip : handleSave}
            disabled={provider !== "facilitator" && !apiKey.trim()}
            style={{
              fontFamily: sans,
              fontSize: type.ui,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: 8,
              border: "none",
              background: (provider === "facilitator" || apiKey.trim()) ? T.terra : "rgba(0,0,0,0.1)",
              color: (provider === "facilitator" || apiKey.trim()) ? T.white : "rgba(0,0,0,0.3)",
              cursor: (provider === "facilitator" || apiKey.trim()) ? "pointer" : "not-allowed",
              transition: "all 0.15s"
            }}
          >
            {provider === "facilitator" ? "Continue with facilitator's key" : "Save and continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("entry");
  const [name, setName] = useState("");
  const [userApiConfig, setUserApiConfig] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showApiModal, setShowApiModal] = useState(false);

  // Check for existing API config on mount
  useEffect(() => {
    const provider = localStorage.getItem("userApiProvider");
    const apiKey = localStorage.getItem("userApiKey");
    if (provider) {
      setUserApiConfig({ provider, apiKey });
    }
  }, []);

  return (
    <GateCheck>
      {/* API Key Modal - Disabled in favor of "Open in Claude/ChatGPT" workflow */}
      {/* {showApiModal && (
        <ApiKeyModal
          onClose={() => setShowApiModal(false)}
          onSave={(config) => setUserApiConfig(config)}
        />
      )} */}
      {screen === "entry" && (
        <EntryScreen
          onJoin={n => { setName(n); setScreen("workshop"); }}
          onFacilitator={n => { setName(n || "Azul"); setScreen("facilitator"); }}
        />
      )}
      {screen === "workshop" && (
        <WorkshopScreen
          name={name}
          onDone={() => setScreen("entry")}
          userApiConfig={userApiConfig}
          onRequestApiSetup={() => setShowApiModal(true)}
        />
      )}
      {screen === "facilitator" && (
        <FacilitatorScreen onExit={() => setScreen("entry")} />
      )}
      <Analytics />
    </GateCheck>
  );
}
